// src/admin/pages/OrderDetailPage.tsx
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  FulfillmentStatus,
  OrderDetail,
  OrderStatus,
  PaymentStatus,
} from "../../types/order";
import { OrdersAPI } from "../../api/orders";
import { assetUrl } from "../../api/http";
import { useViewportMatch } from "../../hooks/useViewportMatch";
import EngravingCustomizationPreview from "../../components/orders/EngravingCustomizationPreview";
import PendantCustomizationPreview from "../../components/orders/PendantCustomizationPreview";
import TshirtCustomizationPreview from "../../components/orders/TshirtCustomizationPreview";
import { Cup3DPreview } from "../../components/Cup3DPreview";
import { LicensePlatePreview } from "../../components/LicensePlatePreview";

// Pretty label helper (e.g., "partially_refunded" → "Partially Refunded")
const pretty = (s: string) =>
  s
    .replace(/_/g, " ")
    .replace(
      /\w\S*/g,
      (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
    );

// Tiny button loader
const Spinner = () => (
  <span
    style={{
      display: "inline-block",
      width: 16,
      height: 16,
      borderRadius: "50%",
      border: "2px solid rgba(255,255,255,0.6)",
      borderTopColor: "white",
      animation: "spin .7s linear infinite",
    }}
  />
);

// Small copy-to-clipboard pill
function Copy({ text, title = "Copy" }: { text?: string; title?: string }) {
  if (!text) return null;
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {}
  };
  return (
    <button
      onClick={onCopy}
      title={title}
      style={{
        marginLeft: 8,
        padding: "2px 8px",
        borderRadius: 999,
        border: "1px solid #e5e7eb",
        background: "#f9fafb",
        fontSize: 12,
        cursor: "pointer",
      }}
    >
      Copy
    </button>
  );
}

function normalizeTrackingNumbers(values: unknown[]): string[] {
  const seen = new Set<string>();
  const cleaned: string[] = [];

  for (const value of values) {
    const trackingNumber = String(value ?? "")
      .trim()
      .replace(/\s+/g, "");
    if (!trackingNumber || seen.has(trackingNumber)) continue;
    seen.add(trackingNumber);
    cleaned.push(trackingNumber);
  }

  return cleaned;
}

function getTrackingNumbers(shipping?: any): string[] {
  if (!shipping) return [];
  if (
    Array.isArray(shipping.trackingNumbers) &&
    shipping.trackingNumbers.length
  ) {
    return normalizeTrackingNumbers(shipping.trackingNumbers);
  }
  if (typeof shipping.trackingNumber === "string") {
    return normalizeTrackingNumbers([shipping.trackingNumber]);
  }
  return [];
}

function toDateTimeInputValue(value?: string | Date): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function toIsoIfPresent(value?: string): string | undefined {
  const raw = String(value || "").trim();
  if (!raw) return undefined;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
}

const WORKFLOW_STATUS_OPTIONS: OrderStatus[] = [
  OrderStatus.Paid,
  OrderStatus.Waiting,
  OrderStatus.CurrentlyWorking,
  OrderStatus.Finishing,
  OrderStatus.WaitingForShipping,
  OrderStatus.Shipped,
  OrderStatus.Delivered,
];

const PICKUP_WORKFLOW_STATUS_OPTIONS: OrderStatus[] = [
  OrderStatus.WaitingForPickup,
  OrderStatus.Waiting,
  OrderStatus.CurrentlyWorking,
  OrderStatus.Finishing,
  OrderStatus.ReadyForPickup,
  OrderStatus.PickedUp,
];

const REFUND_REASON_OPTIONS = [
  "Customer requested cancellation",
  "Duplicate order or payment",
  "Product unavailable",
  "Order issue or defect",
  "Shipping or delivery issue",
  "Fraud or risk review",
  "Goodwill adjustment",
  "Other",
];

const isWorkflowStatus = (value: string): value is OrderStatus =>
  [...WORKFLOW_STATUS_OPTIONS, ...PICKUP_WORKFLOW_STATUS_OPTIONS].includes(
    value as OrderStatus,
  );

const isPickupOrder = (order: any) =>
  String(order?.deliveryMethod || "shipping").toLowerCase() === "pickup";

const inferWorkflowStatus = (order: any): OrderStatus => {
  const status = String(order?.status || "")
    .trim()
    .toLowerCase();
  const options = isPickupOrder(order)
    ? PICKUP_WORKFLOW_STATUS_OPTIONS
    : WORKFLOW_STATUS_OPTIONS;
  if (options.includes(status as OrderStatus)) return status as OrderStatus;
  if (isPickupOrder(order) && status === OrderStatus.Paid) {
    return OrderStatus.WaitingForPickup;
  }
  if (!isPickupOrder(order) && isWorkflowStatus(status)) return status;

  const fulfillment = String(order?.fulfillmentStatus || "")
    .trim()
    .toLowerCase();
  if (isPickupOrder(order)) {
    if (status === OrderStatus.PickedUp || fulfillment === FulfillmentStatus.Fulfilled) {
      return OrderStatus.PickedUp;
    }
    return OrderStatus.WaitingForPickup;
  }
  if (fulfillment === FulfillmentStatus.Delivered) return OrderStatus.Delivered;
  if (fulfillment === FulfillmentStatus.Shipped) return OrderStatus.Shipped;
  return OrderStatus.Paid;
};

const fulfillmentFromWorkflowStatus = (
  status: OrderStatus
): FulfillmentStatus => {
  if (status === OrderStatus.Delivered) return FulfillmentStatus.Delivered;
  if (status === OrderStatus.Shipped) return FulfillmentStatus.Shipped;
  if (status === OrderStatus.WaitingForShipping) return FulfillmentStatus.Fulfilled;
  if (status === OrderStatus.PickedUp) return FulfillmentStatus.Fulfilled;
  return FulfillmentStatus.Unfulfilled;
};

const canPurgeOrderImages = (order: Partial<OrderDetail> | null | undefined) => {
  if (!order) return false;
  return (
    String(order.status || "").trim().toLowerCase() === OrderStatus.Cancelled ||
    String(order.fulfillmentStatus || "").trim().toLowerCase() ===
      FulfillmentStatus.Delivered ||
    Boolean((order as any)?.shipping?.deliveredAt)
  );
};

type SourceAsset = {
  key: string;
  label: string;
  url: string;
  filename?: string;
  mime?: string;
  size?: number;
};

function safeAssetUrl(url?: string): string {
  if (!url) return "";
  return assetUrl(url) || url;
}

function collectCustomizationSourceAssets(options: Record<string, unknown>) {
  const found: SourceAsset[] = [];
  const push = (
    key: string,
    label: string,
    rawUrl?: string,
    meta?: { filename?: string; mime?: string; size?: number }
  ) => {
    const url = safeAssetUrl(rawUrl);
    if (!url) return;
    found.push({
      key,
      label,
      url,
      filename: meta?.filename,
      mime: meta?.mime,
      size: meta?.size,
    });
  };

  const photo = (options as any)?.photoCustomization || {};
  push("photo-main", "Main Upload", photo.sourceImageUrl || photo.imageUrl, {
    filename: photo.sourceImageFilename,
    mime: photo.sourceImageMimeType,
    size: photo.sourceImageSize,
  });

  const legacyLicense = (options as any)?.licensePlateCustomization || {};
  push(
    "license-main",
    "License Upload",
    legacyLicense.sourceImageUrl || legacyLicense.imageUrl
  );

  const phoneCase = (options as any)?.phoneCaseCustomization || {};
  push("phone-main", "Phone Case Upload", phoneCase.sourceImageUrl || phoneCase.imageUrl, {
    filename: phoneCase.sourceImageFilename,
    mime: phoneCase.sourceImageMimeType,
    size: phoneCase.sourceImageSize,
  });

  const pendant = (options as any)?.pendantCustomization || {};
  push(
    "pendant-front-upload",
    "Pendant Front Upload",
    pendant?.frontPhoto?.sourceImageUrl || pendant?.frontPhoto?.imageUrl,
    {
      filename: pendant?.frontPhoto?.sourceImageFilename,
      mime: pendant?.frontPhoto?.sourceImageMimeType,
      size: pendant?.frontPhoto?.sourceImageSize,
    }
  );
  push(
    "pendant-back-upload",
    "Pendant Back Upload",
    pendant?.backPhoto?.sourceImageUrl || pendant?.backPhoto?.imageUrl,
    {
      filename: pendant?.backPhoto?.sourceImageFilename,
      mime: pendant?.backPhoto?.sourceImageMimeType,
      size: pendant?.backPhoto?.sourceImageSize,
    }
  );
  push("pendant-back-template", "Pendant Back Template", pendant?.backImage);

  const license = (options as any)?.licenseCustomization || {};
  push("license-modern", "License Upload", license?.image?.sourceImageUrl || license?.image?.imageUrl, {
    filename: license?.image?.sourceImageFilename,
    mime: license?.image?.sourceImageMimeType,
    size: license?.image?.sourceImageSize,
  });

  const phoneCover = (options as any)?.phoneCoverCustomization || {};
  push("phone-modern", "Phone Case Upload", phoneCover?.image?.sourceImageUrl || phoneCover?.image?.imageUrl, {
    filename: phoneCover?.image?.sourceImageFilename,
    mime: phoneCover?.image?.sourceImageMimeType,
    size: phoneCover?.image?.sourceImageSize,
  });

  const tshirt = (options as any)?.tshirtCustomization || {};
  push("tshirt-front", "T-shirt Front Upload", tshirt?.front?.image?.sourceImageUrl || tshirt?.front?.image?.imageUrl, {
    filename: tshirt?.front?.image?.sourceImageFilename,
    mime: tshirt?.front?.image?.sourceImageMimeType,
    size: tshirt?.front?.image?.sourceImageSize,
  });
  push("tshirt-back", "T-shirt Back Upload", tshirt?.back?.image?.sourceImageUrl || tshirt?.back?.image?.imageUrl, {
    filename: tshirt?.back?.image?.sourceImageFilename,
    mime: tshirt?.back?.image?.sourceImageMimeType,
    size: tshirt?.back?.image?.sourceImageSize,
  });

  const originals = (options as any)?.originalUploads || {};
  push("orig-main", "Original Upload", originals?.main?.sourceImageUrl, {
    filename: originals?.main?.sourceImageFilename,
    mime: originals?.main?.sourceImageMimeType,
    size: originals?.main?.sourceImageSize,
  });
  push("orig-front", "Original Front Upload", originals?.front?.sourceImageUrl, {
    filename: originals?.front?.sourceImageFilename,
    mime: originals?.front?.sourceImageMimeType,
    size: originals?.front?.sourceImageSize,
  });
  push("orig-back", "Original Back Upload", originals?.back?.sourceImageUrl, {
    filename: originals?.back?.sourceImageFilename,
    mime: originals?.back?.sourceImageMimeType,
    size: originals?.back?.sourceImageSize,
  });

  const byKey = new Map<string, SourceAsset>();
  for (const row of found) {
    if (!row.url) continue;
    if (byKey.has(row.url)) continue;
    byKey.set(row.url, row);
  }
  return Array.from(byKey.values());
}

function formatMoney(value: number, currency: string) {
  return `${Number(value || 0).toFixed(2)} ${currency}`;
}

function formatOptionLabel(value: string) {
  return pretty(value.replace(/([A-Z])/g, " $1").trim());
}

function formatSimpleOptionValue(value: unknown): string {
  if (Array.isArray(value)) {
    const simpleValues = value.filter(
      (item) =>
        typeof item === "string" ||
        typeof item === "number" ||
        typeof item === "boolean"
    );
    return simpleValues.map(String).join(", ");
  }
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value);
  }
  return "";
}

function getReadableOptionEntries(options: Record<string, unknown>) {
  const hiddenKeys = new Set([
    "photoCustomization",
    "licensePlateCustomization",
    "pendantCustomization",
    "cupCustomization",
    "licenseCustomization",
    "phoneCaseCustomization",
    "phoneCaseTextStyle",
    "phoneCoverCustomization",
    "tshirtCustomization",
    "engravingCustomization",
    "itemSpecs",
    "giftWrap",
  ]);
  return Object.entries(options)
    .filter(([key, value]) => {
      if (hiddenKeys.has(key) || value == null || value === "") return false;
      return formatSimpleOptionValue(value).trim().length > 0;
    })
    .map(([key, value]) => ({
      label: formatOptionLabel(key),
      value: formatSimpleOptionValue(value),
    }));
}

function valueText(value: unknown): string {
  return String(value ?? "").trim();
}

function firstText(...values: unknown[]) {
  for (const value of values) {
    const text = valueText(value);
    if (text) return text;
  }
  return "";
}

function firstAssetUrl(...values: unknown[]) {
  for (const value of values) {
    const text = valueText(value);
    if (!text) continue;
    return safeAssetUrl(text);
  }
  return "";
}

function addShareLine(lines: string[], label: string, value?: unknown) {
  const text = valueText(value);
  if (!text) return;
  lines.push(`${label}: ${text}`);
}

type OrderDetailLine = { label: string; value?: unknown };

function formatDetailWithSuffix(value: unknown, suffix: string) {
  const text = valueText(value);
  if (!text) return "";
  return text.endsWith(suffix) ? text : `${text}${suffix}`;
}

function addDetailLine(
  details: OrderDetailLine[],
  seen: Set<string>,
  label: string,
  value?: unknown,
) {
  const text = valueText(value);
  if (!text) return;
  const key = `${label}:${text}`.toLowerCase();
  if (seen.has(key)) return;
  seen.add(key);
  details.push({ label, value: text });
}

function formatDetailMoney(value: unknown) {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return "";
  return `+$${amount.toFixed(2)}`;
}

function formatChainValue(value: unknown): string {
  if (!value) return "";
  if (typeof value === "string" || typeof value === "number") {
    return String(value).trim();
  }
  if (typeof value !== "object") return "";
  const chain = value as Record<string, any>;
  return firstText(
    chain.chainName,
    chain.chainType,
    chain.name,
    chain.label,
    [chain.chainColor, chain.chainSku].filter(Boolean).join(" "),
  );
}

function formatLengthValue(value: unknown): string {
  if (value == null || value === "") return "";
  if (Array.isArray(value)) {
    return value.map(formatLengthValue).filter(Boolean).join(", ");
  }
  if (typeof value === "object") {
    const row = value as Record<string, unknown>;
    return firstText(
      formatLengthValue(row.chainLengthInch),
      formatLengthValue(row.chainLengthInches),
      formatLengthValue(row.chainLength),
      formatLengthValue(row.lengthInch),
      formatLengthValue(row.lengthInches),
      formatLengthValue(row.length),
      formatLengthValue(row.selectedLengthInch),
      formatLengthValue(row.selectedLengthInches),
      formatLengthValue(row.selectedLength),
      formatLengthValue(row.selectedSize),
      formatLengthValue(row.size),
    );
  }
  const raw = String(value).trim();
  if (!raw) return "";
  return /\bin\b/i.test(raw) ? raw : `${raw} in`;
}

function formatSingleLengthValue(value: unknown): string {
  if (Array.isArray(value)) {
    return value.length === 1 ? formatLengthValue(value[0]) : "";
  }
  return formatLengthValue(value);
}

function resolveSelectedChainLength(
  lineItem: any,
  snapshot: any,
  options: Record<string, any>,
): string {
  const pendant = options?.pendantCustomization || {};
  const engraving = options?.engravingCustomization || {};
  const selectedChain = firstText(
    formatLengthValue(pendant?.selectedChain),
    formatLengthValue(engraving?.selectedChain),
    formatLengthValue(engraving?.matchingChain),
    formatLengthValue((options as any)?.selectedChain),
  );
  return firstText(
    formatLengthValue(pendant?.chainLengthInch),
    formatLengthValue(pendant?.chainLengthInches),
    formatLengthValue(pendant?.chainLength),
    formatLengthValue(pendant?.lengthInch),
    formatLengthValue(pendant?.length),
    formatLengthValue(pendant?.selectedLength),
    formatLengthValue(pendant?.selectedSize),
    formatLengthValue(engraving?.chainLengthInch),
    formatLengthValue(engraving?.chainLengthInches),
    formatLengthValue(engraving?.chainLength),
    formatLengthValue(engraving?.lengthInch),
    formatLengthValue(engraving?.length),
    formatLengthValue(engraving?.selectedLength),
    formatLengthValue(engraving?.selectedSize),
    selectedChain,
    formatLengthValue((options as any)?.chainLengthInch),
    formatLengthValue((options as any)?.chainLengthInches),
    formatLengthValue((options as any)?.chainLength),
    formatLengthValue((options as any)?.lengthInch),
    formatLengthValue((options as any)?.length),
    formatLengthValue((options as any)?.selectedLength),
    formatLengthValue((options as any)?.selectedSize),
    formatLengthValue((options as any)?.size),
    formatLengthValue(lineItem?.selectedSize),
    formatSingleLengthValue(snapshot?.chainLengthInch),
    formatSingleLengthValue(snapshot?.chainLengthInches),
  );
}

function getDiscountCodeLabels(order: OrderDetail): string[] {
  const discounts = (order as any).discounts || {};
  const codes = new Set<string>();
  const push = (value: unknown, prefix = "") => {
    const code = String(value || "").trim().toUpperCase();
    if (!code) return;
    codes.add(prefix ? `${prefix}: ${code}` : code);
  };

  push(discounts?.coupon?.code, "Coupon");
  push(discounts?.referral?.referralCode, "Referral");
  if (Array.isArray(discounts?.lines)) {
    discounts.lines.forEach((line: any) => {
      const type = String(line?.type || "").toLowerCase();
      const prefix =
        type === "coupon"
          ? "Coupon"
          : type.includes("referral")
            ? "Referral"
            : "Code";
      push(line?.code, prefix);
    });
  }

  return Array.from(codes);
}

function engravingSideText(side: any) {
  return firstText(
    side?.sanitizedText,
    side?.text,
    Array.isArray(side?.lines) ? side.lines.filter(Boolean).join(" / ") : "",
  );
}

function addEngravingSideDetails(
  details: OrderDetailLine[],
  seen: Set<string>,
  label: string,
  side: any,
) {
  if (side?.enabled === false) return;

  const text = engravingSideText(side);
  const hasDetails = Boolean(
    side?.enabled ||
      text ||
      side?.fontFamily ||
      side?.fontKey ||
      side?.fontSize != null ||
      side?.fittedFontSize != null ||
      side?.textLimit != null ||
      side?.maxLines != null ||
      side?.lineHeight != null ||
      side?.letterSpacingPx != null ||
      side?.normalizedLetterSpacingEm != null ||
      side?.normalizedFontSizePct != null,
  );
  if (!hasDetails) return;

  addDetailLine(details, seen, `${label} Engraving Text`, text || "(blank)");
  addDetailLine(
    details,
    seen,
    `${label} Engraving Font`,
    firstText(side?.fontFamily, side?.fontKey),
  );
  addDetailLine(
    details,
    seen,
    `${label} Engraving Text Size`,
    formatDetailWithSuffix(firstText(side?.fittedFontSize, side?.fontSize), "px"),
  );
  addDetailLine(details, seen, `${label} Engraving Max Lines`, side?.maxLines);
  addDetailLine(details, seen, `${label} Engraving Character Limit`, side?.textLimit);
  addDetailLine(details, seen, `${label} Engraving Line Height`, side?.lineHeight);
  addDetailLine(
    details,
    seen,
    `${label} Engraving Letter Spacing`,
    firstText(side?.letterSpacingPx, side?.normalizedLetterSpacingEm),
  );
  addDetailLine(
    details,
    seen,
    `${label} Engraving Font Scale`,
    formatDetailWithSuffix(side?.normalizedFontSizePct, "%"),
  );
  addDetailLine(
    details,
    seen,
    `${label} Engraving X Offset`,
    formatDetailWithSuffix(side?.contentOffsetXPct, "%"),
  );
  addDetailLine(
    details,
    seen,
    `${label} Engraving Y Offset`,
    formatDetailWithSuffix(side?.contentOffsetYPct, "%"),
  );
  addDetailLine(details, seen, `${label} Engraving Scale`, side?.contentScale);
  if (side?.overflowed) {
    addDetailLine(details, seen, `${label} Engraving Overflow Warning`, "Yes");
  }
  if (side?.hasUnsupportedGlyphs) {
    addDetailLine(
      details,
      seen,
      `${label} Engraving Unsupported Characters`,
      "Yes",
    );
  }
}

function collectEngravingDetailEntries(
  lineItem: any,
  snapshot: any,
  options: Record<string, any>,
): OrderDetailLine[] {
  const details: OrderDetailLine[] = [];
  const seen = new Set<string>();
  const legacy = lineItem?.engraving || {};
  const customization = options?.engravingCustomization;
  const pendantBack =
    options?.pendantCustomization?.backEngraving ||
    options?.pendantCustomization?.backEngravingCustomization ||
    {};
  const snapshotEngraving = snapshot?.engraving || {};

  const legacyText = firstText(
    legacy?.text,
    legacy?.frontText,
    snapshotEngraving?.text,
  );
  if (
    (legacy?.enabled ?? snapshotEngraving?.enabled) !== false &&
    (legacy?.enabled || legacyText || legacy?.font || legacy?.fontSize)
  ) {
    addDetailLine(details, seen, "Engraving Text", legacyText || "(blank)");
    addDetailLine(
      details,
      seen,
      "Engraving Font",
      firstText(legacy?.font, snapshotEngraving?.font),
    );
    addDetailLine(
      details,
      seen,
      "Engraving Text Size",
      formatDetailWithSuffix(
        firstText(legacy?.fontSize, snapshotEngraving?.fontSize),
        "px",
      ),
    );
    addDetailLine(details, seen, "Engraving Side", legacy?.side);
    addDetailLine(details, seen, "Engraving Extra", formatDetailMoney(legacy?.extraCost));
  }

  // A normal pendant chain length is not evidence of engraving customization.
  if (
    customization &&
    typeof customization === "object" &&
    Object.keys(customization).length > 0 &&
    customization.enabled !== false
  ) {
    addDetailLine(details, seen, "Engraving Active Side", customization?.activeSide);
    addDetailLine(details, seen, "Engraving Selected Color", customization?.selectedColor);
    addDetailLine(
      details,
      seen,
      "Engraving Matching Chain",
      formatChainValue(customization?.selectedChain),
    );
    addDetailLine(
      details,
      seen,
      "Engraving Chain Length",
      resolveSelectedChainLength(lineItem, snapshot, options),
    );
    addEngravingSideDetails(details, seen, "Front", customization?.front);
    addEngravingSideDetails(details, seen, "Back", customization?.back);
    if (customization?.back?.enabled !== false) {
      addDetailLine(
        details,
        seen,
        "Back Engraving Extra",
        formatDetailMoney(customization?.backEngravingExtraCost),
      );
    }
  }

  const pendantBackText = firstText(pendantBack?.text);
  if (
    pendantBack?.enabled !== false &&
    (pendantBack?.enabled ||
      pendantBackText ||
      pendantBack?.font ||
      pendantBack?.fontSize ||
      pendantBack?.sizePx)
  ) {
    addDetailLine(details, seen, "Back Engraving Text", pendantBackText || "(blank)");
    addDetailLine(details, seen, "Back Engraving Font", pendantBack?.font);
    addDetailLine(
      details,
      seen,
      "Back Engraving Text Size",
      formatDetailWithSuffix(firstText(pendantBack?.fontSize, pendantBack?.sizePx), "px"),
    );
    addDetailLine(details, seen, "Back Engraving Color", pendantBack?.color);
    addDetailLine(
      details,
      seen,
      "Back Engraving Letter Spacing",
      formatDetailWithSuffix(pendantBack?.letterSpacingPx, "px"),
    );
    addDetailLine(details, seen, "Back Engraving Line Height", pendantBack?.lineHeight);
    addDetailLine(details, seen, "Back Engraving Text Align", pendantBack?.textAlign);
    addDetailLine(
      details,
      seen,
      "Back Engraving X Position",
      formatDetailWithSuffix(pendantBack?.xPct, "%"),
    );
    addDetailLine(
      details,
      seen,
      "Back Engraving Y Position",
      formatDetailWithSuffix(pendantBack?.yPct, "%"),
    );
  }

  return details;
}

function addUniqueImageLine(
  lines: string[],
  seen: Set<string>,
  label: string,
  value?: unknown,
) {
  const url = firstAssetUrl(value);
  if (!url || seen.has(url)) return;
  seen.add(url);
  addShareLine(lines, label, url);
}

function formatShareAddress(addr: ResolvedAddress | null) {
  if (!addr) return "";
  return [
    addr.name,
    addr.line1,
    addr.line2,
    [addr.city, addr.state, addr.postalCode].filter(Boolean).join(", "),
    addr.country,
  ]
    .filter(Boolean)
    .join("\n");
}

function buildOrderShareText(
  order: OrderDetail,
  address: ResolvedAddress | null,
  customerEmail: string,
) {
  const lines: string[] = [];
  const customerPhone = firstText(
    order.customer?.phone,
    address?.phone,
    (order as any).lookupPhone,
    (order as any).phone
  );
  const formattedAddress = formatShareAddress(address);

  addShareLine(lines, "Order", order.orderNumber);
  addShareLine(lines, "Phone", customerPhone);
  addShareLine(lines, "Email", customerEmail);
  if (formattedAddress) {
    lines.push("Address:");
    lines.push(formattedAddress);
  }
  lines.push("");
  lines.push(`Items (${order.items?.length || 0})`);

  (order.items || []).forEach((item, index) => {
    const li = item as any;
    const snap = li.snapshot || {};
    const options = (li.selectedOptions || {}) as Record<string, any>;
    const pendant = options.pendantCustomization || {};
    const frontPhoto = pendant.frontPhoto || pendant.image || options.photoCustomization || {};
    const backPhoto = pendant.backPhoto || {};
    const photoCustomization = options.photoCustomization || {};
    const licenseImage =
      options.licenseCustomization?.image || options.licensePlateCustomization || {};
    const phoneImage =
      options.phoneCoverCustomization?.image || options.phoneCaseCustomization || {};
    const cupImage = options.cupCustomization?.image || {};
    const tshirtFront = options.tshirtCustomization?.front?.image || {};
    const tshirtBack = options.tshirtCustomization?.back?.image || {};
    const imageSeen = new Set<string>();
    const chainLength = resolveSelectedChainLength(li, snap, options);
    const selectedChain = firstText(
      formatChainValue(pendant.selectedChain),
      formatChainValue(options.engravingCustomization?.selectedChain),
      formatChainValue(options.selectedChain),
      formatChainValue(pendant.chainType),
      formatChainValue(options.chain),
      formatChainValue(snap.chainType)
    );
    const type = firstText(
      pendant.pendantType,
      pendant.type,
      pendant.shape,
      snap.pendantType,
      snap.type,
      li.categoryLabel,
      item.category
    );
    const engravingDetails = collectEngravingDetailEntries(li, snap, options);

    lines.push("");
    lines.push(`${index + 1}. ${firstText(snap.name, li.name, item.productId)}`);
    addShareLine(lines, "Color", firstText(options.color, pendant.color, snap.color));
    addShareLine(lines, "Type", type);
    addShareLine(lines, "Chain Type", selectedChain);
    addShareLine(
      lines,
      "Chain Color",
      firstText(
        options.engravingCustomization?.selectedChain?.chainColor,
        pendant.chainColor,
        options.chainColor,
        options.engravingCustomization?.selectedColor,
        options.color,
        snap.color
      )
    );
    addShareLine(
      lines,
      "Chain SKU",
      firstText(
        options.engravingCustomization?.selectedChain?.chainSku,
        pendant.chainSku,
        options.chainSku
      )
    );
    addShareLine(lines, "Chain Length", chainLength);
    engravingDetails.forEach((detail) => {
      addShareLine(lines, detail.label, detail.value);
    });

    addUniqueImageLine(lines, imageSeen, "Front Uploaded Image", frontPhoto.sourceImageUrl);
    addUniqueImageLine(lines, imageSeen, "Back Uploaded Image", backPhoto.sourceImageUrl);
    addUniqueImageLine(lines, imageSeen, "Uploaded Image", photoCustomization.sourceImageUrl);
    addUniqueImageLine(lines, imageSeen, "Uploaded Image", licenseImage.sourceImageUrl);
    addUniqueImageLine(lines, imageSeen, "Uploaded Image", phoneImage.sourceImageUrl);
    addUniqueImageLine(lines, imageSeen, "Uploaded Image", cupImage.sourceImageUrl);
    addUniqueImageLine(lines, imageSeen, "Front Uploaded Image", tshirtFront.sourceImageUrl);
    addUniqueImageLine(lines, imageSeen, "Back Uploaded Image", tshirtBack.sourceImageUrl);

    addUniqueImageLine(lines, imageSeen, "Front Position Screenshot", frontPhoto.imageUrl);
    addUniqueImageLine(lines, imageSeen, "Back Position Screenshot", backPhoto.imageUrl);
    addUniqueImageLine(lines, imageSeen, "Position Screenshot", photoCustomization.imageUrl);
    addUniqueImageLine(lines, imageSeen, "Position Screenshot", licenseImage.imageUrl);
    addUniqueImageLine(lines, imageSeen, "Position Screenshot", phoneImage.imageUrl);
    addUniqueImageLine(lines, imageSeen, "Position Screenshot", cupImage.imageUrl);
    addUniqueImageLine(lines, imageSeen, "Front Position Screenshot", tshirtFront.imageUrl);
    addUniqueImageLine(lines, imageSeen, "Back Position Screenshot", tshirtBack.imageUrl);
  });

  return lines.join("\n");
}

/** ------- NEW: Resolve shipping address from various shapes ------- */
type ResolvedAddress = {
  name?: string;
  phone?: string;
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
};

function normalizeAddress(raw: any): ResolvedAddress | null {
  if (!raw) return null;

  const name =
    raw.name ||
    [raw.firstName, raw.lastName].filter(Boolean).join(" ") ||
    raw.fullName ||
    raw.recipient ||
    undefined;

  const phone = raw.phone || raw.mobile || raw.telephone || undefined;

  const line1 =
    raw.line1 ||
    raw.address1 ||
    raw.addressLine1 ||
    raw.street ||
    raw.street1 ||
    raw.address ||
    undefined;

  const line2 =
    raw.line2 || raw.address2 || raw.addressLine2 || raw.street2 || undefined;

  const city = raw.city || raw.town || raw.locality || undefined;

  const state =
    raw.state ||
    raw.stateCode ||
    raw.province ||
    raw.provinceCode ||
    raw.region ||
    undefined;

  const postalCode =
    raw.postalCode || raw.zip || raw.zipCode || raw.postcode || undefined;

  const country =
    raw.country ||
    raw.countryName ||
    raw.countryCode ||
    (typeof raw.country === "object" ? raw.country?.name : undefined) ||
    undefined;

  const anyValue =
    name || phone || line1 || line2 || city || state || postalCode || country;

  if (!anyValue) return null;
  return { name, phone, line1, line2, city, state, postalCode, country };
}

function resolveShippingAddress(order: any): ResolvedAddress | null {
  if (!order) return null;

  const candidates = [
    order.shipping?.address,
    order.shippingAddress,
    order.address?.shipping,
    order.customer?.shippingAddress,
    order.address,
    order.customer?.defaultShippingAddress,
    order?.shipping,
  ];

  for (const c of candidates) {
    const n = normalizeAddress(c);
    if (n) return n;
  }
  return null;
}

/** Address block renderer */
function AddressBlock({ addr }: { addr: ResolvedAddress | null }) {
  if (!addr) {
    return (
      <div style={{ fontSize: 12, color: "#6b7280" }}>
        No shipping address on file.
      </div>
    );
  }
  const { name, phone, line1, line2, city, state, postalCode, country } = addr;
  return (
    <div style={{ fontSize: 14, lineHeight: 1.5, color: "#111827" }}>
      {name && <div style={{ fontWeight: 700 }}>{name}</div>}
      {line1 && <div>{line1}</div>}
      {line2 && <div>{line2}</div>}
      {(city || state || postalCode) && (
        <div>{[city, state, postalCode].filter(Boolean).join(", ")}</div>
      )}
      {country && <div>{country}</div>}
      {phone && (
        <div style={{ marginTop: 4, color: "#6b7280", fontSize: 12 }}>
          {phone}
        </div>
      )}
    </div>
  );
}

export default function OrderDetailPage() {
  const isCompact = useViewportMatch("(max-width: 960px)");
  const { idOrNumber } = useParams();
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(false);

  // Email sending controls
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailTemplate, setEmailTemplate] = useState<
    "placed" | "paid" | "delivered" | "status_update"
  >("placed");
  const [customSubject, setCustomSubject] = useState("");
  const [customMessage, setCustomMessage] = useState(""); // treat as HTML
  const [sendingCustom, setSendingCustom] = useState(false);
  const [includeSummary, setIncludeSummary] = useState(true);
  const [savingStatuses, setSavingStatuses] = useState(false);
  const [savingShipping, setSavingShipping] = useState(false);
  const [syncingTracking, setSyncingTracking] = useState(false);
  const [savingRefund, setSavingRefund] = useState(false);
  const [refundPanelOpen, setRefundPanelOpen] = useState(false);
  const [purgingImages, setPurgingImages] = useState(false);
  const [orderShareStatus, setOrderShareStatus] = useState("");
  const [statusDraft, setStatusDraft] = useState({
    workflowStatus: OrderStatus.Paid as OrderStatus,
  });
  const [sendWorkflowEmail, setSendWorkflowEmail] = useState(false);
  const [refundDraft, setRefundDraft] = useState({
    mode: "full" as "full" | "partial",
    amount: "",
    reason: "",
    note: "",
    processorRefundId: "",
    refundedAt: "",
  });

  const [shipping, setShipping] = useState({
    methodCode: "",
    methodLabel: "",
    carrier: "",
    trackingNumbers: [] as string[],
    newTrackingNumber: "",
    estimatedDelivery: "",
    shippedAt: "",
    deliveredAt: "",
  });

  const [notes, setNotes] = useState("");
  const [tags, setTags] = useState<string>("");

  // Load order
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!idOrNumber) return;
      setLoading(true);
      try {
        const data = await OrdersAPI.get(idOrNumber);
        if (!mounted) return;

        setOrder(data);

        setNotes((data as any).notes || "");
        setTags(((data as any).tags || []).join(", "));
        setStatusDraft({
          workflowStatus: inferWorkflowStatus(data),
        });

        setShipping({
          methodCode: (data as any).shipping?.methodCode || "",
          methodLabel: (data as any).shipping?.methodLabel || "",
          carrier: (data as any).shipping?.carrier || "",
          trackingNumbers: getTrackingNumbers((data as any).shipping),
          newTrackingNumber: "",
          estimatedDelivery: toDateTimeInputValue(
            (data as any).shipping?.estimatedDelivery
          ),
          shippedAt: toDateTimeInputValue((data as any).shipping?.shippedAt),
          deliveredAt: toDateTimeInputValue((data as any).shipping?.deliveredAt),
        });
      } catch (e) {
        console.error(e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [idOrNumber]);

  // —— Actions
  const saveShipping = async () => {
    if (!order) return;
    try {
      setSavingShipping(true);
      const trackingNumbers = normalizeTrackingNumbers([
        ...shipping.trackingNumbers,
        shipping.newTrackingNumber,
      ]);
      const updated = await OrdersAPI.updateShipping(order._id, {
        methodCode: shipping.methodCode,
        methodLabel: shipping.methodLabel,
        carrier: shipping.carrier,
        trackingNumbers,
        estimatedDelivery: shipping.estimatedDelivery
          ? toIsoIfPresent(shipping.estimatedDelivery)
          : null,
        shippedAt: shipping.shippedAt
          ? toIsoIfPresent(shipping.shippedAt)
          : null,
        deliveredAt: shipping.deliveredAt
          ? toIsoIfPresent(shipping.deliveredAt)
          : null,
      });
      setOrder(updated);
      setShipping({
        methodCode: (updated as any).shipping?.methodCode || "",
        methodLabel: (updated as any).shipping?.methodLabel || "",
        carrier: (updated as any).shipping?.carrier || "",
        trackingNumbers: getTrackingNumbers((updated as any).shipping),
        newTrackingNumber: "",
        estimatedDelivery: toDateTimeInputValue(
          (updated as any).shipping?.estimatedDelivery
        ),
        shippedAt: toDateTimeInputValue((updated as any).shipping?.shippedAt),
        deliveredAt: toDateTimeInputValue((updated as any).shipping?.deliveredAt),
      });
      setStatusDraft({ workflowStatus: inferWorkflowStatus(updated) });
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "Failed to update shipping (server error).";
      alert(msg);
      console.error("updateShipping error:", e);
    } finally {
      setSavingShipping(false);
    }
  };

  const refreshCarrierTracking = async () => {
    if (!order) return;
    setSyncingTracking(true);
    try {
      const result = await OrdersAPI.syncShippingTracking(order._id);
      if (result.order) {
        setOrder(result.order);
        setShipping((current) => ({
          ...current,
          estimatedDelivery: toDateTimeInputValue(
            (result.order as any).shipping?.estimatedDelivery,
          ),
          deliveredAt: toDateTimeInputValue(
            (result.order as any).shipping?.deliveredAt,
          ),
        }));
        setStatusDraft({ workflowStatus: inferWorkflowStatus(result.order) });
      }
      if (result.tracking.errors?.length) {
        alert(`Tracking refreshed with a note: ${result.tracking.errors[0]}`);
      }
    } catch (e: any) {
      alert(
        e?.response?.data?.message ||
          e?.message ||
          "Could not refresh carrier tracking.",
      );
    } finally {
      setSyncingTracking(false);
    }
  };

  const updateTrackingNumber = (index: number, value: string) => {
    setShipping((current) => ({
      ...current,
      trackingNumbers: current.trackingNumbers.map((trackingNumber, i) =>
        i === index ? value : trackingNumber
      ),
    }));
  };

  const removeTrackingNumber = (index: number) => {
    setShipping((current) => ({
      ...current,
      trackingNumbers: current.trackingNumbers.filter((_, i) => i !== index),
    }));
  };

  const addTrackingNumberDraft = () => {
    setShipping((current) => {
      const nextTrackingNumbers = normalizeTrackingNumbers([
        ...current.trackingNumbers,
        current.newTrackingNumber,
      ]);
      return {
        ...current,
        trackingNumbers: nextTrackingNumbers,
        newTrackingNumber: "",
      };
    });
  };

  const saveStatuses = async () => {
    if (!order) return;
    setSavingStatuses(true);
    try {
      const workflowStatus = statusDraft.workflowStatus;
      const updated = await OrdersAPI.updateStatuses(order._id, {
        status: workflowStatus,
        paymentStatus: PaymentStatus.Paid,
        fulfillmentStatus: fulfillmentFromWorkflowStatus(workflowStatus),
      });
      let nextOrder = updated;
      if (sendWorkflowEmail && customerEmail) {
        const result = await OrdersAPI.sendEmail(
          updated._id,
          "status_update"
        );
        if (result.order) nextOrder = result.order;
      }
      setOrder(nextOrder);
      setStatusDraft({
        workflowStatus: inferWorkflowStatus(nextOrder),
      });
      setShipping((prev) => ({
        ...prev,
        deliveredAt: toDateTimeInputValue((nextOrder as any).shipping?.deliveredAt),
      }));
      if (sendWorkflowEmail) {
        alert(
          customerEmail
            ? "Workflow saved and customer status email sent."
            : "Workflow saved. No customer email is on file, so no email was sent."
        );
      }
      setSendWorkflowEmail(false);
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "Failed to update statuses (server error).";
      alert(msg);
      console.error("updateStatuses error:", e);
    } finally {
      setSavingStatuses(false);
    }
  };

  const saveRefund = async () => {
    if (!order) return;
    const totals: any = (order as any).totals || {};
    const summary: any = (order as any).refundSummary || {};
    const grandTotal = Number(totals.grandTotal || 0);
    const refundedTotal = Number(summary.totalRefunded || 0);
    const remaining = Math.max(0, Number((grandTotal - refundedTotal).toFixed(2)));
    const fullRefund = refundDraft.mode === "full";
    const amount = fullRefund ? remaining : Number(refundDraft.amount || 0);

    if (remaining <= 0) {
      alert("This order has no refundable amount remaining.");
      return;
    }
    if (!fullRefund && (!Number.isFinite(amount) || amount <= 0)) {
      alert("Enter a refund amount greater than zero.");
      return;
    }
    if (!fullRefund && amount > remaining) {
      alert(`Refund amount cannot exceed ${remaining.toFixed(2)} ${order.currency}.`);
      return;
    }

    const label = fullRefund
      ? `${remaining.toFixed(2)} ${order.currency}`
      : `${amount.toFixed(2)} ${order.currency}`;
    const confirmed = window.confirm(
      `Record a ${fullRefund ? "full" : "partial"} refund of ${label} for ${order.orderNumber}? This updates dashboard earnings and keeps an audit record.`
    );
    if (!confirmed) return;

    setSavingRefund(true);
    try {
      const updated = await OrdersAPI.updateStatuses(order._id, {
        paymentStatus: fullRefund
          ? PaymentStatus.Refunded
          : PaymentStatus.PartiallyRefunded,
        refund: {
          amount: fullRefund ? undefined : amount,
          fullRefund,
          reason:
            refundDraft.reason.trim() ||
            (fullRefund ? "Full transaction refund." : "Partial transaction refund."),
          note: refundDraft.note.trim() || undefined,
          processor: refundDraft.processorRefundId.trim() ? "stripe" : "manual",
          processorRefundId: refundDraft.processorRefundId.trim() || undefined,
          refundedAt: toIsoIfPresent(refundDraft.refundedAt),
        },
      });
      setOrder(updated);
      setStatusDraft({ workflowStatus: inferWorkflowStatus(updated) });
      setRefundDraft({
        mode: "full",
        amount: "",
        reason: "",
        note: "",
        processorRefundId: "",
        refundedAt: "",
      });
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "Failed to record refund (server error).";
      alert(msg);
      console.error("saveRefund error:", e);
    } finally {
      setSavingRefund(false);
    }
  };

  const saveMeta = async () => {
    if (!order) return;
    try {
      const updated = await OrdersAPI.updateMeta(order._id, {
        notes,
        tags: tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
      });
      setOrder(updated);
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "Failed to update meta (server error).";
      alert(msg);
      console.error("updateMeta error:", e);
    }
  };

  // NEW: send email to customer
  const customerEmail = order?.customer?.email || order?.lookupEmail || "";
  const deliveredInviteSent = Boolean((order as any)?.shipping?.deliveredEmailSentAt);
  const sendCustomerEmail = async () => {
    if (!order) return;
    if (emailTemplate === "delivered" && deliveredInviteSent) {
      alert("Delivered review invitation was already sent.");
      return;
    }
    setSendingEmail(true);
    try {
      const result = await OrdersAPI.sendEmail(order._id, emailTemplate);
      if (result.order) setOrder(result.order);
      alert(
        result.alreadySent
          ? "Delivered review invitation was already sent."
          : "Email sent to customer."
      );
    } catch (e: any) {
      const msg =
        e?.response?.data?.message || e?.message || "Failed to send email.";
      alert(msg);
      console.error("sendEmail error:", e);
    } finally {
      setSendingEmail(false);
    }
  };

  const sendCustom = async () => {
    if (!order) return;
    if (!customSubject.trim() || !customMessage.trim()) {
      alert("Subject and message are required.");
      return;
    }
    setSendingCustom(true);
    try {
      await OrdersAPI.sendCustomEmail(order._id, {
        subject: customSubject.trim(),
        message: customMessage, // HTML allowed
        includeOrderSummary: includeSummary,
      });
      alert("Custom email sent.");
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "Failed to send custom email.";
      alert(msg);
      console.error("sendCustomEmail error:", e);
    } finally {
      setSendingCustom(false);
    }
  };

  const purgeUploadedPhotos = async () => {
    if (!order) return;
    const expected = order.orderNumber;
    const typed = window.prompt(
      `This permanently deletes uploaded photos stored for ${expected}. Type ${expected} to continue.`
    );
    if (typed !== expected) return;

    setPurgingImages(true);
    try {
      const result = await OrdersAPI.purgeOrderImages(order._id);
      const refreshed = await OrdersAPI.get(order._id);
      setOrder(refreshed);
      alert(
        `Removed ${result.removedImageRefs} image reference(s) and deleted ${result.deletedFiles} file(s).`
      );
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "Failed to purge uploaded photos.";
      alert(msg);
      console.error("purgeOrderImages error:", e);
    } finally {
      setPurgingImages(false);
    }
  };

  const shipAddr = useMemo(
    () => (order ? resolveShippingAddress(order as any) : null),
    [order]
  );
  const orderShareText = useMemo(
    () =>
      order
        ? buildOrderShareText(order, shipAddr, customerEmail)
        : "",
    [customerEmail, order, shipAddr]
  );

  const shareOrderDetails = async () => {
    if (!order || !orderShareText.trim()) return;
    const nav = navigator as Navigator & {
      share?: (data: { title?: string; text?: string }) => Promise<void>;
    };
    const title = "Share Order Details";

    try {
      if (typeof nav.share === "function") {
        await nav.share({ title, text: orderShareText });
        setOrderShareStatus("Shared");
      } else {
        await navigator.clipboard.writeText(orderShareText);
        setOrderShareStatus("Copied order details");
      }
      window.setTimeout(() => setOrderShareStatus(""), 2500);
    } catch (e: any) {
      if (e?.name === "AbortError") return;
      try {
        await navigator.clipboard.writeText(orderShareText);
        setOrderShareStatus("Copied order details");
        window.setTimeout(() => setOrderShareStatus(""), 2500);
      } catch {
        alert("Could not share or copy the order details.");
      }
    }
  };

  if (loading) return <div>Loading…</div>;
  if (!order) return <div>Not found</div>;

  // ---- Safe totals (avoid TS errors if fields aren't in the type) ----
  const t: any = (order as any).totals || {};
  const currency: string = (order as any).currency || t.currency || "USD";

  const subtotal = Number(t.subtotal ?? 0);
  const shippingTotal = Number(t.shippingTotal ?? 0);
  const taxTotal = Number(t.taxTotal ?? 0);
  const discountTotal = Number(t.discountTotal ?? 0);
  const discountCodeLabels = getDiscountCodeLabels(order);
  const feesTotal = Number(t.feesTotal ?? 0);
  const grandTotal = Number(t.grandTotal ?? 0);
  const refundSummary: any = (order as any).refundSummary || {};
  const refundedTotal = Number(refundSummary.totalRefunded || 0);
  const netTotal = Number(
    refundSummary.netTotal ?? Math.max(0, grandTotal - refundedTotal)
  );
  const refundableRemaining = Math.max(
    0,
    Number((grandTotal - refundedTotal).toFixed(2))
  );
  const refunds = Array.isArray((order as any).refunds)
    ? ((order as any).refunds as any[])
    : [];
  const isFullyRefunded =
    String(order.paymentStatus || "").toLowerCase() === PaymentStatus.Refunded ||
    Boolean(refundSummary.fullyRefunded);
  const refundAmountValue =
    refundDraft.mode === "full"
      ? refundableRemaining > 0
        ? refundableRemaining.toFixed(2)
        : ""
      : refundDraft.amount;
  const refundActionAmount =
    refundDraft.mode === "full"
      ? refundableRemaining
      : Number(refundDraft.amount || 0);
  const canRecordRefund =
    !savingRefund &&
    refundableRemaining > 0 &&
    (refundDraft.mode === "full" ||
      (Number.isFinite(refundActionAmount) &&
        refundActionAmount > 0 &&
        refundActionAmount <= refundableRemaining));
  const deliveryMethod =
    String((order as any).deliveryMethod || "shipping").toLowerCase() === "pickup"
      ? "pickup"
      : "shipping";
  const deliverySpeed =
    String((order as any).deliverySpeed || "normal").toLowerCase() === "express"
      ? "express"
      : "normal";
  const shippingLabel =
    deliveryMethod === "pickup"
      ? "Pickup"
      : deliverySpeed === "express"
        ? "Express Delivery"
        : "Normal Delivery";
  const pickupSelection = (order as any).pickup || null;
  const workflowStatusOptions =
    deliveryMethod === "pickup"
      ? PICKUP_WORKFLOW_STATUS_OPTIONS
      : WORKFLOW_STATUS_OPTIONS;

  return (
    <div
      style={{
        padding: isCompact ? 12 : 16,
        maxWidth: 1480,
        width: "100%",
        boxSizing: "border-box",
        overflowX: "hidden",
        margin: "0 auto",
        background: isFullyRefunded ? "#fff7f7" : undefined,
      }}
    >
      <Link to="/orders" style={{ textDecoration: "none", color: "#2563eb" }}>
        ← Back to Orders
      </Link>

      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <h1 style={{ marginTop: 8, fontSize: 22, fontWeight: 700 }}>
          {order.orderNumber}
        </h1>

        {/* quick payment status badge next to title */}
        <span
          style={{
            display: "inline-block",
            padding: "2px 8px",
            borderRadius: 999,
            background: isFullyRefunded
              ? "#fee2e2"
              : (order.paymentStatus || "").toLowerCase() === "paid"
                ? "#ecfdf5"
                : "#fff7ed",
            color: isFullyRefunded
              ? "#991b1b"
              : (order.paymentStatus || "").toLowerCase() === "paid"
                ? "#065f46"
                : "#9a3412",
            fontSize: 12,
            fontWeight: 700,
            lineHeight: 1.7,
          }}
        >
          {pretty(order.paymentStatus || "unpaid")}
        </span>
        {isFullyRefunded ? (
          <span
            style={{
              display: "inline-block",
              padding: "2px 8px",
              borderRadius: 999,
              background: "#f3f4f6",
              color: "#4b5563",
              fontSize: 12,
              fontWeight: 700,
              lineHeight: 1.7,
            }}
          >
            Inactive
          </span>
        ) : null}

      </div>

      {/* Secondary meta row: lookup + email + created */}
      <div
        style={{
          display: "flex",
          gap: 16,
          flexWrap: "wrap",
          alignItems: "center",
          color: "#374151",
          fontSize: 13,
          marginTop: 4,
          marginBottom: 8,
        }}
      >
        <div>
          <span style={{ color: "#6b7280" }}>Lookup Code:</span>{" "}
          <code
            style={{
              background: "#f3f4f6",
              border: "1px solid #e5e7eb",
              borderRadius: 6,
              padding: "2px 6px",
            }}
          >
            {(order as any).lookupCode || "—"}
          </code>
          <Copy text={(order as any).lookupCode} title="Copy lookup code" />
        </div>

        <div>
          <span style={{ color: "#6b7280" }}>Customer Email:</span>{" "}
          <span style={{ overflowWrap: "anywhere" }}>{customerEmail || "—"}</span>
          <Copy text={customerEmail} title="Copy email" />
        </div>

        <div>
          <span style={{ color: "#6b7280" }}>Created:</span>{" "}
          <time>
            {new Date(order.createdAt).toLocaleString("en-US", {
              timeZone: "America/Chicago",
            })}
          </time>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isCompact ? "minmax(0, 1fr)" : "1fr 1fr",
          gap: 16,
        }}
      >
        {/* LEFT: Payment + Totals + Email actions */}
        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            padding: 16,
            minWidth: 0,
          }}
        >
          <h3 style={{ marginTop: 0 }}>Payment & Totals</h3>

          <div style={{ display: "grid", gap: 8 }}>
            <div>
              <span style={{ color: "#6b7280" }}>Payment:</span>{" "}
              <strong>{pretty(order.paymentStatus)}</strong>
              {order.payment?.provider && (
                <span style={{ marginLeft: 8, color: "#6b7280" }}>
                  via {order.payment.provider}
                </span>
              )}
              {order.payment?.methodLabel && (
                <span style={{ marginLeft: 8, color: "#374151" }}>
                  ({order.payment.methodLabel})
                </span>
              )}
            </div>

            {order.payment?.intentId && (
              <div style={{ fontSize: 12, color: "#6b7280" }}>
                Intent: <code>{order.payment.intentId}</code>
              </div>
            )}
            {order.payment?.chargeId && (
              <div style={{ fontSize: 12, color: "#6b7280" }}>
                Charge: <code>{order.payment.chargeId}</code>
              </div>
            )}
          </div>

          <div style={{ height: 10 }} />

          <div
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: 10,
              padding: 12,
              background: "#fafafa",
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 8 }}>
              Order Workflow Status
            </div>
            <div
              style={{
                display: "grid",
                gap: 8,
                gridTemplateColumns: "minmax(0, 1fr)",
              }}
            >
              <select
                value={statusDraft.workflowStatus}
                onChange={(e) =>
                  setStatusDraft((prev) => ({
                    ...prev,
                    workflowStatus: e.target.value as OrderStatus,
                  }))
                }
                style={{
                  padding: 8,
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                }}
              >
                {workflowStatusOptions.map((value) => (
                  <option key={value} value={value}>
                    {pretty(value)}
                  </option>
                ))}
              </select>
              <div style={{ fontSize: 12, color: "#6b7280" }}>
                Payment is kept as <strong>{pretty(PaymentStatus.Paid)}</strong>
                {isFullyRefunded
                  ? " unless a refund is already recorded."
                  : deliveryMethod === "pickup"
                    ? ". Pickup orders use pickup statuses instead of shipping statuses."
                    : "."}
              </div>
              <label
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 8,
                  fontSize: 12,
                  color: "#374151",
                  lineHeight: 1.4,
                }}
              >
                <input
                  type="checkbox"
                  checked={sendWorkflowEmail}
                  onChange={(e) => setSendWorkflowEmail(e.target.checked)}
                  disabled={!customerEmail || isFullyRefunded}
                  style={{ marginTop: 2 }}
                />
                <span>
                  Email customer after saving this status
                  <span style={{ display: "block", color: "#6b7280" }}>
                    Sends a thankful update saying the order is currently{" "}
                    <strong>{pretty(statusDraft.workflowStatus)}</strong>.
                  </span>
                </span>
              </label>
            </div>
            <div style={{ marginTop: 8 }}>
              <button
                onClick={saveStatuses}
                disabled={savingStatuses || isFullyRefunded}
                style={{
                  padding: "8px 12px",
                  borderRadius: 8,
                  border: "1px solid #e5e7eb",
                  background:
                    savingStatuses || isFullyRefunded ? "#6b7280" : "#111827",
                  color: "white",
                  fontWeight: 600,
                  cursor:
                    savingStatuses || isFullyRefunded ? "not-allowed" : "pointer",
                }}
              >
                {savingStatuses
                  ? "Saving..."
                  : isFullyRefunded
                    ? "Refunded"
                    : "Save Workflow"}
              </button>
            </div>
          </div>

          <div style={{ height: 12 }} />

          <div
            style={{
              border: `1px solid ${isFullyRefunded ? "#ef4444" : "#fecaca"}`,
              borderRadius: 10,
              padding: refundPanelOpen ? 12 : "10px 12px",
              background: isFullyRefunded ? "#fef2f2" : "#fff7ed",
              display: "grid",
              gap: refundPanelOpen ? 12 : 0,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 10,
                flexWrap: "wrap",
                alignItems: "flex-start",
              }}
            >
              <div>
                <div style={{ fontWeight: 800, color: "#9f1239" }}>
                  Refund Management
                </div>
                <div style={{ fontSize: 12, color: "#7f1d1d", marginTop: 3 }}>
                  {refundedTotal > 0
                    ? `${refundedTotal.toFixed(2)} ${currency} refunded · ${netTotal.toFixed(
                        2
                      )} ${currency} net`
                    : "Collapsed by default. Expand only when a refund needs attention."}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {isFullyRefunded ? (
                  <span
                    style={{
                      border: "1px solid #fecaca",
                      borderRadius: 999,
                      background: "#fee2e2",
                      color: "#991b1b",
                      padding: "4px 9px",
                      fontSize: 12,
                      fontWeight: 800,
                    }}
                  >
                    Fully Refunded
                  </span>
                ) : null}
                <button
                  type="button"
                  onClick={() => setRefundPanelOpen((open) => !open)}
                  style={{
                    border: "1px solid #fed7aa",
                    borderRadius: 999,
                    background: "white",
                    color: "#9a3412",
                    padding: "5px 10px",
                    fontSize: 12,
                    fontWeight: 800,
                    cursor: "pointer",
                  }}
                >
                  {refundPanelOpen ? "Hide Refund Options" : "Show Refund Options"}
                </button>
              </div>
            </div>

            {refundPanelOpen ? (
              <>
                <div
                  style={{
                    display: "grid",
                    gap: 8,
                    gridTemplateColumns: isCompact
                      ? "minmax(0, 1fr)"
                      : "repeat(3, minmax(0, 1fr))",
                  }}
                >
                  {[
                    ["Already Refunded", refundedTotal],
                    ["Net Order Total", netTotal],
                    ["Refundable", refundableRemaining],
                  ].map(([label, value]) => (
                    <div
                      key={String(label)}
                      style={{
                        border: "1px solid #fed7aa",
                        borderRadius: 8,
                        background: "rgba(255,255,255,.74)",
                        padding: 10,
                        minWidth: 0,
                      }}
                    >
                      <div
                        style={{ fontSize: 11, color: "#9a3412", fontWeight: 700 }}
                      >
                        {label}
                      </div>
                      <div
                        style={{
                          marginTop: 4,
                          color: "#111827",
                          fontSize: 18,
                          fontWeight: 850,
                        }}
                      >
                        {Number(value || 0).toFixed(2)} {currency}
                      </div>
                    </div>
                  ))}
                </div>

                <div style={{ display: "grid", gap: 8 }}>
                  <div style={{ fontSize: 12, color: "#7f1d1d", fontWeight: 800 }}>
                    Refund Type
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gap: 8,
                      gridTemplateColumns: isCompact
                        ? "minmax(0, 1fr)"
                        : "repeat(2, minmax(0, 1fr))",
                    }}
                  >
                    {[
                      {
                        value: "full",
                        title: "Full refund",
                        detail:
                          "Refunds the full remaining order amount and marks the order inactive.",
                      },
                      {
                        value: "partial",
                        title: "Partial refund",
                        detail:
                          "Refunds a specific amount and keeps the order in a partial refund state.",
                      },
                    ].map((option) => {
                      const selected = refundDraft.mode === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() =>
                            setRefundDraft((prev) => ({
                              ...prev,
                              mode: option.value as "full" | "partial",
                              amount:
                                option.value === "full"
                                  ? refundableRemaining.toFixed(2)
                                  : "",
                            }))
                          }
                          disabled={savingRefund || refundableRemaining <= 0}
                          style={{
                            textAlign: "left",
                            border: selected
                              ? "2px solid #be123c"
                              : "1px solid #fed7aa",
                            borderRadius: 8,
                            background: selected ? "#fff1f2" : "white",
                            color: "#111827",
                            padding: 12,
                            cursor:
                              savingRefund || refundableRemaining <= 0
                                ? "not-allowed"
                                : "pointer",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              fontWeight: 850,
                            }}
                          >
                            <span
                              aria-hidden="true"
                              style={{
                                width: 14,
                                height: 14,
                                borderRadius: 999,
                                border: selected
                                  ? "4px solid #be123c"
                                  : "1px solid #d1d5db",
                                boxSizing: "border-box",
                                background: "white",
                                flex: "0 0 auto",
                              }}
                            />
                            {option.title}
                          </div>
                          <div
                            style={{ marginTop: 5, fontSize: 12, color: "#6b7280" }}
                          >
                            {option.detail}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

              <div
                style={{
                  display: "grid",
                  gap: 8,
                  gridTemplateColumns: isCompact
                    ? "minmax(0, 1fr)"
                    : "repeat(2, minmax(0, 1fr))",
                }}
              >
                <label style={{ display: "grid", gap: 4, minWidth: 0 }}>
                  <span style={{ fontSize: 12, color: "#7f1d1d", fontWeight: 800 }}>
                    Refund Amount
                  </span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder={`Amount (${currency})`}
                    value={refundAmountValue}
                    readOnly={refundDraft.mode === "full"}
                    onChange={(e) =>
                      setRefundDraft((prev) => ({
                        ...prev,
                        mode: "partial",
                        amount: e.target.value,
                      }))
                    }
                    style={{
                      padding: 8,
                      border: "1px solid #fed7aa",
                      borderRadius: 8,
                      minWidth: 0,
                      background: refundDraft.mode === "full" ? "#f9fafb" : "white",
                      color: refundDraft.mode === "full" ? "#6b7280" : "#111827",
                    }}
                  />
                </label>
                <label style={{ display: "grid", gap: 4, minWidth: 0 }}>
                  <span style={{ fontSize: 12, color: "#7f1d1d", fontWeight: 800 }}>
                    Refund Reason
                  </span>
                  <select
                    value={refundDraft.reason}
                    onChange={(e) =>
                      setRefundDraft((prev) => ({ ...prev, reason: e.target.value }))
                    }
                    style={{
                      padding: 8,
                      border: "1px solid #fed7aa",
                      borderRadius: 8,
                      minWidth: 0,
                      background: "white",
                    }}
                  >
                    <option value="">Select a reason</option>
                    {REFUND_REASON_OPTIONS.map((reason) => (
                      <option key={reason} value={reason}>
                        {reason}
                      </option>
                    ))}
                  </select>
                </label>
                <input
                  placeholder="Processor refund ID"
                  value={refundDraft.processorRefundId}
                  onChange={(e) =>
                    setRefundDraft((prev) => ({
                      ...prev,
                      processorRefundId: e.target.value,
                    }))
                  }
                  style={{
                    padding: 8,
                    border: "1px solid #fed7aa",
                    borderRadius: 8,
                    minWidth: 0,
                  }}
                />
                <input
                  type="datetime-local"
                  value={refundDraft.refundedAt}
                  onChange={(e) =>
                    setRefundDraft((prev) => ({
                      ...prev,
                      refundedAt: e.target.value,
                    }))
                  }
                  style={{
                    padding: 8,
                    border: "1px solid #fed7aa",
                    borderRadius: 8,
                    minWidth: 0,
                  }}
                />
                <textarea
                  placeholder="Internal refund note"
                  value={refundDraft.note}
                  onChange={(e) =>
                    setRefundDraft((prev) => ({ ...prev, note: e.target.value }))
                  }
                  rows={2}
                  style={{
                    padding: 8,
                    border: "1px solid #fed7aa",
                    borderRadius: 8,
                    minWidth: 0,
                    gridColumn: isCompact ? "auto" : "1 / -1",
                    resize: "vertical",
                  }}
                />
              </div>

              <div
                style={{
                  border: "1px solid #fed7aa",
                  borderRadius: 8,
                  background: "white",
                  padding: 10,
                  display: "grid",
                  gap: 4,
                  fontSize: 12,
                  color: "#374151",
                }}
              >
                <div style={{ fontWeight: 850, color: "#111827" }}>
                  Refund Preview
                </div>
                <div>
                  {refundDraft.mode === "full" ? "Full refund" : "Partial refund"} ·{" "}
                  {Number(refundActionAmount || 0).toFixed(2)} {currency}
                </div>
                <div>
                  Net total after refund:{" "}
                  <strong>
                    {Math.max(0, netTotal - Number(refundActionAmount || 0)).toFixed(
                      2
                    )}{" "}
                    {currency}
                  </strong>
                </div>
                {refundDraft.mode === "full" ? (
                  <div style={{ color: "#991b1b", fontWeight: 700 }}>
                    The order will be marked refunded, returned, and inactive.
                  </div>
                ) : null}
              </div>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={saveRefund}
                  disabled={!canRecordRefund}
                  style={{
                    padding: "8px 12px",
                    borderRadius: 8,
                    border: "1px solid #be123c",
                    background: canRecordRefund ? "#be123c" : "#fda4af",
                    color: "white",
                    fontWeight: 700,
                    cursor: canRecordRefund ? "pointer" : "not-allowed",
                  }}
                >
                  {savingRefund ? "Recording..." : "Record Refund"}
                </button>
                {refundDraft.mode === "partial" &&
                refundActionAmount > refundableRemaining ? (
                  <span
                    style={{ alignSelf: "center", color: "#b91c1c", fontSize: 12 }}
                  >
                    Amount exceeds refundable balance.
                  </span>
                ) : null}
              </div>
            </>
            ) : null}

            {refundPanelOpen && refunds.length ? (
              <div style={{ display: "grid", gap: 8 }}>
                <div style={{ fontWeight: 700, color: "#7f1d1d" }}>
                  Refund History
                </div>
                {refunds.map((refund, index) => (
                  <div
                    key={refund.refundId || refund.processorRefundId || index}
                    style={{
                      border: "1px solid #fed7aa",
                      borderRadius: 8,
                      background: "white",
                      padding: 10,
                      display: "grid",
                      gap: 4,
                      fontSize: 12,
                    }}
                  >
                    <div style={{ fontWeight: 800, color: "#111827" }}>
                      {Number(refund.amount || 0).toFixed(2)}{" "}
                      {refund.currency || currency}
                    </div>
                    <div style={{ color: "#374151" }}>
                      {refund.reason || "Refund recorded"} ·{" "}
                      {refund.refundedAt
                        ? new Date(refund.refundedAt).toLocaleString()
                        : "No date"}
                    </div>
                    <div style={{ color: "#6b7280" }}>
                      {refund.source || "admin"}
                      {refund.processor ? ` via ${refund.processor}` : ""}
                      {refund.processorRefundId
                        ? ` · ${refund.processorRefundId}`
                        : ""}
                    </div>
                    {Array.isArray(refund.items) && refund.items.length ? (
                      <div style={{ color: "#6b7280" }}>
                        Items:{" "}
                        {refund.items
                          .map((item: any) => {
                            const qty = Number(item.quantity || 0);
                            return `${item.name || item.productId || "Item"}${
                              qty ? ` x${qty}` : ""
                            }`;
                          })
                          .join(", ")}
                      </div>
                    ) : null}
                    {refund.note ? (
                      <div style={{ color: "#6b7280" }}>{refund.note}</div>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          <div style={{ height: 12 }} />

          {/* Totals */}
          <div style={{ display: "grid", gap: 8, fontSize: 14 }}>
            <div>
              Order Total:{" "}
              <strong>
                {grandTotal.toFixed(2)} {currency}
              </strong>
            </div>
            <div>
              Subtotal:{" "}
              <strong>
                {subtotal.toFixed(2)} {currency}
              </strong>
            </div>
            <div>
              {shippingLabel}:{" "}
              <strong>
                {shippingTotal.toFixed(2)} {currency}
              </strong>
            </div>
            <div>
              Tax:{" "}
              <strong>
                {taxTotal.toFixed(2)} {currency}
              </strong>
            </div>
            {discountTotal > 0 && (
              <div>
                Discount:{" "}
                <strong>
                  {discountTotal.toFixed(2)} {currency}
                </strong>
                {discountCodeLabels.length > 0 ? (
                  <span style={{ marginLeft: 8, color: "#475569", fontSize: 13 }}>
                    ({discountCodeLabels.join(", ")})
                  </span>
                ) : null}
              </div>
            )}
            {feesTotal > 0 && (
              <div>
                Fees:{" "}
                <strong>
                  {feesTotal.toFixed(2)} {currency}
                </strong>
              </div>
            )}
            {refundedTotal > 0 && (
              <>
                <div>
                  Refunded:{" "}
                  <strong style={{ color: "#dc2626" }}>
                    -{refundedTotal.toFixed(2)} {currency}
                  </strong>
                </div>
                <div>
                  Net Total:{" "}
                  <strong>
                    {netTotal.toFixed(2)} {currency}
                  </strong>
                </div>
              </>
            )}
          </div>

          <div style={{ height: 16 }} />

          {/* Email customer */}
          <div>
            <h4 style={{ margin: "8px 0" }}>Email Customer</h4>
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <select
                value={emailTemplate}
                onChange={(e) =>
                  setEmailTemplate(
                    e.target.value as
                      | "placed"
                      | "paid"
                      | "delivered"
                      | "status_update"
                  )
                }
                style={{
                  padding: 8,
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  maxWidth: "100%",
                }}
              >
                <option value="placed">Order Placed</option>
                <option value="paid">Payment Confirmed</option>
                <option value="status_update">Working / Status Update</option>
                <option value="delivered">Delivered + Review Invite</option>
              </select>
              {emailTemplate === "status_update" ? (
                <span style={{ fontSize: 12, color: "#475569", lineHeight: 1.4 }}>
                  Sends the customer the current order status:{" "}
                  <strong>{pretty(statusDraft.workflowStatus)}</strong>
                </span>
              ) : null}
	              <button
	                onClick={sendCustomerEmail}
	                disabled={
	                  sendingEmail ||
	                  !customerEmail ||
	                  (emailTemplate === "delivered" && deliveredInviteSent)
	                }
	                title={
	                  !customerEmail
	                    ? "No email on file"
	                    : emailTemplate === "delivered" && deliveredInviteSent
	                      ? "Delivered review invitation already sent"
	                      : "Send template email"
	                }
                style={{
                  padding: "8px 12px",
                  borderRadius: 8,
                  border: "1px solid #e5e7eb",
	                  background:
	                    sendingEmail ||
	                    (emailTemplate === "delivered" && deliveredInviteSent)
	                      ? "#6b7280"
	                      : "#111827",
                  color: "white",
                  fontWeight: 600,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
	                  cursor:
	                    sendingEmail ||
	                    (emailTemplate === "delivered" && deliveredInviteSent)
	                      ? "not-allowed"
	                      : "pointer",
	                }}
	              >
	                {sendingEmail && <Spinner />}
	                {emailTemplate === "delivered" && deliveredInviteSent
	                  ? "Invitation Sent"
	                  : "Send Email"}
	              </button>
	              {deliveredInviteSent ? (
	                <span
	                  style={{
	                    padding: "4px 8px",
	                    borderRadius: 999,
	                    background: "#dcfce7",
	                    color: "#166534",
	                    fontSize: 12,
	                    fontWeight: 700,
	                  }}
	                >
	                  Delivered + invitation sent
	                </span>
	              ) : null}
	            </div>
          </div>

          <div style={{ height: 16 }} />

          <div
            style={{
              border: "1px solid #fecaca",
              background: "#fff1f2",
              borderRadius: 10,
              padding: 12,
              display: "grid",
              gap: 8,
            }}
          >
            <div style={{ fontWeight: 700, color: "#9f1239" }}>
              Uploaded Photo Cleanup
            </div>
            <div style={{ fontSize: 12, color: "#881337", lineHeight: 1.5 }}>
              Permanently removes customer-uploaded photos from storage for this
              order. Enabled only after the order is cancelled or completed.
            </div>
            <button
              type="button"
              onClick={purgeUploadedPhotos}
              disabled={purgingImages || !canPurgeOrderImages(order)}
              style={{
                justifySelf: "start",
                padding: "8px 12px",
                borderRadius: 8,
                border: "1px solid #be123c",
                background:
                  purgingImages || !canPurgeOrderImages(order)
                    ? "#fda4af"
                    : "#be123c",
                color: "white",
                fontWeight: 600,
                cursor:
                  purgingImages || !canPurgeOrderImages(order)
                    ? "not-allowed"
                    : "pointer",
              }}
            >
              {purgingImages ? "Deleting Photos..." : "Delete Uploaded Photos"}
            </button>
          </div>
        </div>

        {/* RIGHT: Delivery */}
        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            padding: 16,
            minWidth: 0,
          }}
        >
          <h3 style={{ marginTop: 0 }}>Delivery</h3>
          <div style={{ marginBottom: 10, fontSize: 13, color: "#374151" }}>
            Method:{" "}
            <strong>
              {deliveryMethod === "pickup" ? "Store Pickup" : shippingLabel}
            </strong>
          </div>

          {deliveryMethod === "pickup" ? (
            <div
              style={{
                padding: 12,
                border: "1px dashed #e5e7eb",
                borderRadius: 10,
                background: "#fafafa",
                display: "grid",
                gap: 6,
                fontSize: 13,
              }}
            >
              <div>
                <strong>Location:</strong>{" "}
                {pickupSelection?.locationLabel || pickupSelection?.locationId || "—"}
              </div>
              <div>
                <strong>Date:</strong> {pickupSelection?.pickupDate || "—"}
              </div>
              <div>
                <strong>Time:</strong> {pickupSelection?.pickupTimeSlot || "—"}
              </div>
              {pickupSelection?.pickupAddress ? (
                <div>
                  <strong>Address:</strong>{" "}
                  {[
                    pickupSelection.pickupAddress.line1,
                    pickupSelection.pickupAddress.line2,
                    pickupSelection.pickupAddress.city,
                    pickupSelection.pickupAddress.state,
                    pickupSelection.pickupAddress.postalCode,
                    pickupSelection.pickupAddress.country,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </div>
              ) : null}
              {pickupSelection?.notes ? (
                <div>
                  <strong>Notes:</strong> {pickupSelection.notes}
                </div>
              ) : null}
            </div>
          ) : (
            <>
              {/* Ship To (read-only) */}
              <div
                style={{
                  padding: 12,
                  border: "1px dashed #e5e7eb",
                  borderRadius: 10,
                  background: "#fafafa",
                  marginBottom: 10,
                }}
              >
                <div style={{ fontWeight: 700, marginBottom: 6 }}>Ship To</div>
                <AddressBlock addr={shipAddr} />
              </div>

              <div style={{ display: "grid", gap: 8 }}>
                <input
                  placeholder="Method code (optional)"
                  value={shipping.methodCode}
                  onChange={(e) =>
                    setShipping((s) => ({ ...s, methodCode: e.target.value }))
                  }
                  style={{
                    padding: 8,
                    border: "1px solid #e5e7eb",
                    borderRadius: 8,
                  }}
                />
                <input
                  placeholder="Method label (optional)"
                  value={shipping.methodLabel}
                  onChange={(e) =>
                    setShipping((s) => ({ ...s, methodLabel: e.target.value }))
                  }
                  style={{
                    padding: 8,
                    border: "1px solid #e5e7eb",
                    borderRadius: 8,
                  }}
                />
                <select
                  aria-label="Carrier"
                  value={shipping.carrier}
                  onChange={(e) =>
                    setShipping((s) => ({ ...s, carrier: e.target.value }))
                  }
                  style={{
                    padding: 8,
                    border: "1px solid #e5e7eb",
                    borderRadius: 8,
                  }}
                >
                  <option value="">Detect carrier from label</option>
                  <option value="USPS">USPS</option>
                  <option value="UPS">UPS</option>
                </select>
                <label style={{ fontSize: 12, color: "#374151" }}>
                  Estimated Delivery
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "minmax(0, 1fr) auto",
                      gap: 8,
                      marginTop: 4,
                    }}
                  >
                    <input
                      type="datetime-local"
                      value={shipping.estimatedDelivery}
                      onChange={(e) =>
                        setShipping((s) => ({
                          ...s,
                          estimatedDelivery: e.target.value,
                        }))
                      }
                      style={{
                        width: "100%",
                        padding: 8,
                        border: "1px solid #e5e7eb",
                        borderRadius: 8,
                      }}
                    />
                    <button
                      type="button"
                      disabled={!shipping.estimatedDelivery}
                      onClick={() =>
                        setShipping((s) => ({ ...s, estimatedDelivery: "" }))
                      }
                      style={{
                        padding: "8px 10px",
                        borderRadius: 8,
                        border: "1px solid #e5e7eb",
                        background: shipping.estimatedDelivery ? "#fff" : "#f8fafc",
                        color: shipping.estimatedDelivery ? "#334155" : "#94a3b8",
                        cursor: shipping.estimatedDelivery ? "pointer" : "not-allowed",
                        fontWeight: 700,
                      }}
                    >
                      Clear
                    </button>
                  </div>
                </label>
                <label style={{ fontSize: 12, color: "#374151" }}>
                  Shipped At
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "minmax(0, 1fr) auto",
                      gap: 8,
                      marginTop: 4,
                    }}
                  >
                    <input
                      type="datetime-local"
                      value={shipping.shippedAt}
                      onChange={(e) =>
                        setShipping((s) => ({ ...s, shippedAt: e.target.value }))
                      }
                      style={{
                        width: "100%",
                        padding: 8,
                        border: "1px solid #e5e7eb",
                        borderRadius: 8,
                      }}
                    />
                    <button
                      type="button"
                      disabled={!shipping.shippedAt}
                      onClick={() =>
                        setShipping((s) => ({ ...s, shippedAt: "" }))
                      }
                      style={{
                        padding: "8px 10px",
                        borderRadius: 8,
                        border: "1px solid #e5e7eb",
                        background: shipping.shippedAt ? "#fff" : "#f8fafc",
                        color: shipping.shippedAt ? "#334155" : "#94a3b8",
                        cursor: shipping.shippedAt ? "pointer" : "not-allowed",
                        fontWeight: 700,
                      }}
                    >
                      Clear
                    </button>
                  </div>
                </label>
                <label style={{ fontSize: 12, color: "#374151" }}>
                  Delivered At
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "minmax(0, 1fr) auto",
                      gap: 8,
                      marginTop: 4,
                    }}
                  >
                    <input
                      type="datetime-local"
                      value={shipping.deliveredAt}
                      onChange={(e) =>
                        setShipping((s) => ({ ...s, deliveredAt: e.target.value }))
                      }
                      style={{
                        width: "100%",
                        padding: 8,
                        border: "1px solid #e5e7eb",
                        borderRadius: 8,
                      }}
                    />
                    <button
                      type="button"
                      disabled={!shipping.deliveredAt}
                      onClick={() =>
                        setShipping((s) => ({ ...s, deliveredAt: "" }))
                      }
                      style={{
                        padding: "8px 10px",
                        borderRadius: 8,
                        border: "1px solid #e5e7eb",
                        background: shipping.deliveredAt ? "#fff" : "#f8fafc",
                        color: shipping.deliveredAt ? "#334155" : "#94a3b8",
                        cursor: shipping.deliveredAt ? "pointer" : "not-allowed",
                        fontWeight: 700,
                      }}
                    >
                      Clear
                    </button>
                  </div>
                </label>
                <div style={{ display: "grid", gap: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#374151" }}>
                    Tracking numbers
                  </div>
                  {shipping.trackingNumbers.length > 0 ? (
                    <div style={{ display: "grid", gap: 8 }}>
                      {shipping.trackingNumbers.map((trackingNumber, index) => (
                        <div
                          key={`tracking-${index}`}
                          style={{
                            display: "grid",
                            gridTemplateColumns: isCompact
                              ? "minmax(0, 1fr)"
                              : "minmax(0, 1fr) auto",
                            gap: 8,
                          }}
                        >
                          <input
                            aria-label={`Tracking number ${index + 1}`}
                            placeholder={`Tracking number ${index + 1}`}
                            value={trackingNumber}
                            onChange={(e) =>
                              updateTrackingNumber(index, e.target.value)
                            }
                            style={{
                              padding: 8,
                              border: "1px solid #e5e7eb",
                              borderRadius: 8,
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => removeTrackingNumber(index)}
                            style={{
                              padding: "8px 12px",
                              borderRadius: 8,
                              border: "1px solid #fecaca",
                              background: "#fff1f2",
                              color: "#be123c",
                              fontWeight: 700,
                              cursor: "pointer",
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div
                      style={{
                        padding: "10px 12px",
                        border: "1px dashed #d1d5db",
                        borderRadius: 8,
                        color: "#6b7280",
                        fontSize: 13,
                      }}
                    >
                      No tracking numbers saved.
                    </div>
                  )}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isCompact
                        ? "minmax(0, 1fr)"
                        : "minmax(0, 1fr) auto",
                      gap: 8,
                    }}
                  >
                    <input
                      placeholder="New tracking number"
                      value={shipping.newTrackingNumber}
                      onChange={(e) =>
                        setShipping((s) => ({
                          ...s,
                          newTrackingNumber: e.target.value,
                        }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addTrackingNumberDraft();
                        }
                      }}
                      style={{
                        padding: 8,
                        border: "1px solid #e5e7eb",
                        borderRadius: 8,
                      }}
                    />
                    <button
                      type="button"
                      onClick={addTrackingNumberDraft}
                      disabled={!shipping.newTrackingNumber.trim()}
                      style={{
                        padding: "8px 12px",
                        borderRadius: 8,
                        border: "1px solid #d1d5db",
                        background: shipping.newTrackingNumber.trim()
                          ? "#f9fafb"
                          : "#f3f4f6",
                        color: shipping.newTrackingNumber.trim()
                          ? "#111827"
                          : "#9ca3af",
                        fontWeight: 700,
                        cursor: shipping.newTrackingNumber.trim()
                          ? "pointer"
                          : "not-allowed",
                      }}
                    >
                      Add
                    </button>
                  </div>
                  <button
                    onClick={saveShipping}
                    disabled={savingShipping}
                    style={{
                      padding: "8px 12px",
                      borderRadius: 8,
                      border: "1px solid #e5e7eb",
                      background: savingShipping ? "#4b5563" : "#111827",
                      color: "white",
                      fontWeight: 600,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      cursor: savingShipping ? "wait" : "pointer",
                    }}
                  >
                    {savingShipping ? "Saving & checking carrier..." : "Save shipping & check carrier"}
                  </button>
                  <button
                    type="button"
                    onClick={refreshCarrierTracking}
                    disabled={
                      syncingTracking ||
                      savingShipping ||
                      shipping.trackingNumbers.length === 0
                    }
                    style={{
                      padding: "8px 12px",
                      borderRadius: 8,
                      border: "1px solid #cbd5e1",
                      background: "#fff",
                      color: "#0f172a",
                      fontWeight: 700,
                      cursor:
                        syncingTracking ||
                        savingShipping ||
                        shipping.trackingNumbers.length === 0
                          ? "not-allowed"
                          : "pointer",
                    }}
                  >
                    {syncingTracking ? "Refreshing tracking..." : "Refresh carrier tracking"}
                  </button>
                  <div
                    style={{
                      fontSize: 12,
                      color: "#64748b",
                      lineHeight: 1.5,
                    }}
                  >
                    Saving a USPS or UPS label checks its carrier immediately. Open shipments are refreshed automatically every 30 minutes.
                  </div>
                </div>
                {(order as any).shipping?.trackingDetails?.length ? (
                  <div
                    style={{
                      display: "grid",
                      gap: 8,
                      border: "1px solid #dbeafe",
                      background: "#f8fbff",
                      padding: 10,
                      borderRadius: 8,
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 800, color: "#1e3a8a" }}>
                      Live carrier updates
                    </div>
                    {(order as any).shipping.trackingDetails.map((detail: any, index: number) => (
                      <div key={`${detail.trackingNumber || index}`} style={{ fontSize: 12, color: "#334155" }}>
                        <strong>{detail.carrier || "Carrier"}</strong>{" "}
                        <span style={{ overflowWrap: "anywhere" }}>{detail.trackingNumber}</span>
                        {detail.statusSummary || detail.status ? (
                          <div style={{ marginTop: 2 }}>{detail.statusSummary || detail.status}</div>
                        ) : null}
                      </div>
                    ))}
                    {(order as any).shipping?.latestTrackingEvent?.eventType ? (
                      <div style={{ fontSize: 12, color: "#475569" }}>
                        Latest: {(order as any).shipping.latestTrackingEvent.eventType}
                      </div>
                    ) : null}
                    {(order as any).shipping?.trackingLastSyncedAt ? (
                      <div style={{ fontSize: 11, color: "#64748b" }}>
                        Synced {new Date((order as any).shipping.trackingLastSyncedAt).toLocaleString()}
                      </div>
                    ) : null}
                  </div>
                ) : null}
                {(order as any).shipping?.trackingLastError ? (
                  <div style={{ fontSize: 12, color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", padding: 8, borderRadius: 8 }}>
                    Carrier tracking note: {(order as any).shipping.trackingLastError}
                  </div>
                ) : null}
                {(order as any).shipping?.deliveredEmailSentAt ? (
                  <div
                    style={{
                      fontSize: 12,
                      color: "#065f46",
                      background: "#ecfdf5",
                      border: "1px solid #bbf7d0",
                      borderRadius: 8,
                      padding: "6px 8px",
                    }}
                  >
                    Delivered email sent:{" "}
                    {new Date((order as any).shipping.deliveredEmailSentAt).toLocaleString()}
                  </div>
                ) : null}
              </div>
            </>
          )}
        </div>
      </div>

      <div style={{ height: 16 }} />

      {/* --- LINE ITEMS --- */}
      <div
        style={{
          border: "1px solid #d2d2d7",
          borderRadius: 12,
          overflow: "hidden",
          background: "#fff",
          minWidth: 0,
        }}
      >
        <div
          style={{
            padding: isCompact ? "14px 14px 10px" : "16px 18px 10px",
            borderBottom: "1px solid #e5e5ea",
          }}
        >
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>
            Line Items
          </h3>
        </div>

        {!isCompact ? (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "84px minmax(0,1fr)",
              gap: 16,
              padding: "11px 18px",
              borderBottom: "1px solid #e5e5ea",
              background: "#f8fafc",
              color: "#6e6e73",
              fontSize: 11,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 0.4,
            }}
          >
            <div>Image</div>
            <div>Item, Customization & Total</div>
          </div>
        ) : null}

        {order.items.map((it, idx) => {
          const li = it as any;
          const snap: any = it.snapshot || {};
          const options = (li.selectedOptions || {}) as Record<string, any>;
          const photoCustomization = (options as any)?.photoCustomization as any;
          const pendantCustomization = (options as any)
            ?.pendantCustomization as any;
          const cupCustomization = (options as any)?.cupCustomization as any;
          const licenseCustomization = (options as any)
            ?.licenseCustomization as any;
          const phoneCaseCustomization = (options as any)
            ?.phoneCaseCustomization as any;
	  const phoneCaseTextStyle = (options as any)
	    ?.phoneCaseTextStyle as any;
          const phoneCoverCustomization = (options as any)
            ?.phoneCoverCustomization as any;
          const giftWrap = (options as any)?.giftWrap as any;
          const engravingCustomization = (options as any)
            ?.engravingCustomization as any;
          const tshirtCustomization = (options as any)
            ?.tshirtCustomization as any;
          const legacyLicenseCustomization = (options as any)
            ?.licensePlateCustomization as any;
          const readableOptions = getReadableOptionEntries(options);
          const sourceAssets = collectCustomizationSourceAssets(options);
          const originalUploads = (options as any)?.originalUploads as any;
          const customerPhotoImageUrl = firstAssetUrl(
            pendantCustomization?.frontPhoto?.sourceImageUrl,
            pendantCustomization?.frontPhoto?.imageUrl,
            pendantCustomization?.image?.sourceImageUrl,
            pendantCustomization?.image?.imageUrl,
            photoCustomization?.sourceImageUrl,
            photoCustomization?.imageUrl,
            originalUploads?.main?.sourceImageUrl,
            originalUploads?.front?.sourceImageUrl,
          );

	  const image =
        pendantCustomization?.preview?.frontImage ||
	    snap.heroImage ||
	    snap.previewImage ||
	    pendantCustomization?.sourceImage ||
	    pendantCustomization?.template?.frontImage ||
	    pendantCustomization?.frontPhoto?.imageUrl ||
	    photoCustomization?.imageUrl ||
	    (Array.isArray(snap.moreImages) && snap.moreImages[0]) ||
	    (Array.isArray(snap.images) && snap.images[0]);

          const details: OrderDetailLine[] = [];
          const engravingDetails = collectEngravingDetailEntries(
            li,
            snap,
            options,
          );

          if (typeof li.remarks === "string" && li.remarks.trim()) {
            details.push({ label: "Customer Remark", value: li.remarks.trim() });
          }
          if (giftWrap?.enabled) {
            details.push({
              label: "Gift Wrap",
              value: `Yes (+$${Number(giftWrap?.fee ?? 0).toFixed(2)})`,
            });
            const message = String(giftWrap?.message || "").trim();
            if (message) {
              details.push({
                label: "Gift Message",
                value: message,
              });
            }
          }

          const cat = pendantCustomization ? "pendant" : String(it.category || snap.category || "").trim().toLowerCase();

          if (cat === "pendant") {
            const pendantChain = firstText(
              formatChainValue(pendantCustomization?.selectedChain),
              formatChainValue(pendantCustomization?.chainType),
              formatChainValue((options as any).chain),
              formatChainValue(snap.chainType),
            );
            const pendantChainLength = firstText(
              resolveSelectedChainLength(li, snap, options),
            );
            if (pendantChain)
              details.push({
                label: "Chain Type",
                value: pendantChain,
              });
            if (pendantChainLength) {
              details.push({
                label: "Selected Length",
                value: pendantChainLength,
              });
            }
            if (pendantCustomization?.color || (options as any).color) {
              details.push({
                label: "Selected Color",
                value: (options as any).color || pendantCustomization?.color,
              });
            }
            details.push(...engravingDetails);
            if (pendantCustomization?.frontPhoto?.imageUrl) {
              details.push({ label: "Front Custom Photo", value: "Yes" });
            } else if (photoCustomization?.imageUrl) {
              details.push({ label: "Custom Photo", value: "Yes" });
            }
            if (pendantCustomization?.backPhoto?.imageUrl) {
              details.push({ label: "Back Custom Photo", value: "Yes" });
            }
          }

          if (cat === "engraving") {
            const subtype = String(snap?.engravingSubtype || "").trim();
            const engravingChain = firstText(
              formatChainValue(engravingCustomization?.selectedChain),
              formatChainValue((options as any).selectedChain),
              formatChainValue((options as any).chain),
              formatChainValue(snap.chainType),
            );
            const engravingChainColor = firstText(
              engravingCustomization?.selectedChain?.chainColor,
              engravingCustomization?.selectedColor,
              (options as any).chainColor,
              (options as any).color,
              snap.color,
            );
            const engravingChainSku = firstText(
              engravingCustomization?.selectedChain?.chainSku,
              (options as any).chainSku,
            );
            const engravingChainLength = resolveSelectedChainLength(
              li,
              snap,
              options,
            );

            if (subtype) details.push({ label: "Subtype", value: pretty(subtype) });
            if (engravingChain) {
              details.push({ label: "Chain Type", value: engravingChain });
            }
            if (engravingChainLength) {
              details.push({ label: "Selected Length", value: engravingChainLength });
            }
            if (engravingChainColor) {
              details.push({ label: "Chain Color", value: engravingChainColor });
            }
            if (engravingChainSku) {
              details.push({ label: "Chain SKU", value: engravingChainSku });
            }
            details.push(...engravingDetails);
          }

          if (cat !== "pendant" && cat !== "engraving" && engravingDetails.length) {
            details.push(...engravingDetails);
          }

          if (cat === "licenseplate" || cat === "license_plate") {
            const plateType =
              licenseCustomization?.plateType ||
              snap.plateType ||
              "standard_car_12x6";
            const licenseImage = licenseCustomization?.image || {};
            details.push({
              label: "Type",
              value:
                plateType === "motorcycle"
                  ? "Motorcycle"
                  : "Standard Car (12x6)",
            });
            if (licenseCustomization?.material || (options as any).material) {
              details.push({
                label: "Material",
                value: licenseCustomization?.material || (options as any).material,
              });
            }
            const lpText =
              licenseCustomization?.text?.value ??
              legacyLicenseCustomization?.text ??
              "";
            if (lpText) details.push({ label: "Custom Text", value: lpText });
            if (
              licenseImage?.imageUrl ||
              legacyLicenseCustomization?.imageUrl
            ) {
              details.push({
                label: "Custom Image",
                value: "Yes",
              });
            }
          }

          if (cat === "phonecover" || cat === "phone_cover") {
            const phoneImage = phoneCoverCustomization?.image || {};
            const phoneText = phoneCoverCustomization?.text || {};
            if (phoneCoverCustomization?.phoneModel || snap.phoneModel) {
              details.push({
                label: "Phone Model",
                value: phoneCoverCustomization?.phoneModel || snap.phoneModel,
              });
            }
            if (phoneCoverCustomization?.caseSize || snap.caseSize) {
              details.push({
                label: "Case Size",
                value: phoneCoverCustomization?.caseSize || snap.caseSize,
              });
            }
            if (phoneCoverCustomization?.finish || snap.finish)
              details.push({
                label: "Finish",
                value: phoneCoverCustomization?.finish || snap.finish,
              });
            if (phoneCaseCustomization?.imageUrl || phoneImage?.imageUrl) {
              details.push({
                label: "Custom Image",
                value: "Yes",
              });
            }
            const phoneTextValue = phoneCaseCustomization?.text || phoneText?.value;
            if (phoneTextValue) {
              details.push({ label: "Text", value: String(phoneTextValue) });
            }
          }

          if (cat === "tshirt") {
            if (snap.size) details.push({ label: "Size", value: snap.size });
            if (snap.tshirtType)
              details.push({ label: "Type", value: snap.tshirtType });
            if (tshirtCustomization?.color || (options as any).color)
              details.push({
                label: "Selected Color",
                value: tshirtCustomization?.color || (options as any).color,
              });
            const frontPart = tshirtCustomization?.front || {};
            const backPart = tshirtCustomization?.back || {};
            const frontImage = frontPart?.image || {};
            const backImage = backPart?.image || {};
            const frontTextRaw = frontPart?.text;
            const backTextRaw = backPart?.text;
            const frontText =
              frontTextRaw && typeof frontTextRaw === "object"
                ? frontTextRaw
                : {};
            const backText =
              backTextRaw && typeof backTextRaw === "object" ? backTextRaw : {};
            const frontTextValue = String(
              typeof frontTextRaw === "string"
                ? frontTextRaw
                : frontText?.value ?? ""
            ).trim();
            const backTextValue = String(
              typeof backTextRaw === "string" ? backTextRaw : backText?.value ?? ""
            ).trim();

            const pushTextStyle = (
              prefix: "Front" | "Back",
              part: any,
              textObj: any
            ) => {
              const font = String(textObj?.font ?? part?.textFont ?? "").trim();
              const sizePx = Number(textObj?.sizePx ?? part?.textSizePx);
              const weight = Number(textObj?.weight ?? part?.textWeight);
              const parts: string[] = [];
              if (font) parts.push(`Font ${font}`);
              if (Number.isFinite(sizePx) && sizePx > 0)
                parts.push(`Size ${Math.round(sizePx)}px`);
              if (Number.isFinite(weight) && weight > 0)
                parts.push(`Weight ${Math.round(weight)}`);
              if (parts.length) {
                details.push({
                  label: `${prefix} Text Style`,
                  value: parts.join(" · "),
                });
              }
            };
            if (frontImage?.imageUrl) {
              details.push({
                label: "Front Image",
                value: "Yes",
              });
            }
            if (backImage?.imageUrl) {
              details.push({
                label: "Back Image",
                value: "Yes",
              });
            }
            if (frontTextValue) {
              details.push({
                label: "Front Text",
                value: frontTextValue,
              });
              pushTextStyle("Front", frontPart, frontText);
            }
            if (backTextValue) {
              details.push({
                label: "Back Text",
                value: backTextValue,
              });
              pushTextStyle("Back", backPart, backText);
            }
          }

          if (cat === "wallet") {
            if (snap.slots != null)
              details.push({ label: "Slots", value: snap.slots });
            if (snap.hasCoinPocket != null)
              details.push({
                label: "Coin Pocket",
                value: snap.hasCoinPocket ? "Yes" : "No",
              });
          }

          if (cat === "cup") {
            if (cupCustomization?.size || snap.size) {
              details.push({
                label: "Size",
                value: cupCustomization?.size || snap.size,
              });
            }
            const cupImage = cupCustomization?.image || {};
            if (cupImage?.imageUrl || photoCustomization?.imageUrl)
              details.push({
                label: "Custom Image",
                value: "Yes",
              });
            if (String(cupImage?.text || "").trim())
              details.push({
                label: "Text",
                value: String(cupImage.text),
              });
          }

	          return (
            <div
              key={idx}
              style={{
                display: "grid",
                gridTemplateColumns: isCompact
                  ? "minmax(0, 1fr)"
                  : "84px minmax(0, 1fr)",
                gap: isCompact ? 12 : 16,
                padding: isCompact ? 16 : "18px",
                borderBottom:
                  idx === order.items.length - 1 ? "none" : "1px solid #e5e5ea",
                alignItems: "start",
                minWidth: 0,
                background: idx % 2 === 0 ? "#fff" : "#fcfcfd",
              }}
            >
              <div
                style={{
                  width: isCompact ? 64 : 72,
                  height: isCompact ? 64 : 72,
                  borderRadius: 8,
                  overflow: "hidden",
                  background: "#f5f5f7",
                  border: "1px solid #e5e5ea",
	                  display: image || !isCompact ? "grid" : "none",
                  placeItems: "center",
                }}
              >
	                {image ? (
                  <img
                    src={assetUrl(image) || image}
                    alt={snap.name || String(it.productId)}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <span style={{ color: "#8e8e93", fontSize: 18 }}>-</span>
                )}
              </div>

              <div
                style={{
                  minWidth: 0,
                  display: "grid",
                  gap: isCompact ? 12 : 16,
	                  gridTemplateColumns: "minmax(0, 1fr)",
                  alignItems: "start",
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 12,
                      flexWrap: "wrap",
                    }}
                  >
                    <div
                      style={{
                        minWidth: 0,
                        flex: "1 1 220px",
                        fontWeight: 700,
                        overflowWrap: "anywhere",
                        color: "#1d1d1f",
                      }}
                    >
                      {snap.name || it.productId}
                    </div>
                    <div
                      style={{
                        flex: "0 0 auto",
                        border: "1px solid #dbeafe",
                        background: "#eff6ff",
                        borderRadius: 10,
                        padding: "7px 10px",
                        textAlign: "right",
                        minWidth: isCompact ? 0 : 104,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 10,
                          color: "#2563eb",
                          textTransform: "uppercase",
                          letterSpacing: 0,
                          fontWeight: 800,
                        }}
                      >
                        Line Total
                      </div>
                      <div
                        style={{
                          marginTop: 2,
                          fontWeight: 800,
                          fontSize: isCompact ? 15 : 16,
                          color: "#1e3a8a",
                        }}
                      >
                        {formatMoney(it.unitPrice * it.quantity, it.currency)}
                      </div>
                    </div>
                  </div>

                {/* row of key facts */}
                <div
                  style={{
                    fontSize: 12,
                    color: "#6b7280",
                    display: "flex",
                    gap: "8px 12px",
                    flexWrap: "wrap",
                    marginTop: 8,
                    overflowWrap: "anywhere",
                  }}
                >
                  <span>
                    <strong>Category:</strong>{" "}
                    {li.categoryLabel || String(it.category)}
                  </span>
                  <span>
                    <strong>Qty:</strong> {it.quantity}
                  </span>
                  <span>
                    <strong>Unit:</strong> {it.unitPrice.toFixed(2)}{" "}
                    {it.currency}
                  </span>
                  <span>
                    <strong>Total:</strong>{" "}
                    {(it.unitPrice * it.quantity).toFixed(2)} {it.currency}
                  </span>
                </div>

                {/* category-specific detail chips */}
                {details.length > 0 && (
                  <div
                    style={{
                      marginTop: 8,
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))",
                      gap: 8,
                    }}
                  >
                    {details.map((d, i) => {
                      if (d.value == null || d.value === "") return null;
                      const isPriorityMessage =
                        d.label === "Customer Remark" || d.label === "Gift Message";
                      return (
                        <div
                          key={`${d.label}-${i}`}
                          style={{
                            fontSize: 12,
                            background: isPriorityMessage ? "#fffbeb" : "#f9fafb",
                            border: isPriorityMessage
                              ? "1px solid #fbbf24"
                              : "1px solid #eef2f7",
                            borderRadius: 8,
                            padding: "8px 10px",
                            minWidth: 0,
                            overflowWrap: "anywhere",
                          }}
                        >
                          <span style={{ color: isPriorityMessage ? "#92400e" : "#6b7280" }}>
                            {d.label}:
                          </span>{" "}
                          <span
                            style={{
                              fontWeight: 600,
                              color: isPriorityMessage ? "#78350f" : "#111827",
                            }}
                          >
                            {String(d.value)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
                </div>

                <div
                  style={{
                    display: "grid",
                    gap: 10,
                    alignContent: "start",
                    minWidth: 0,
                    maxWidth: "100%",
                  }}
                >
                  {cat === "pendant" && (
                    <PendantCustomizationPreview
                      selectedOptions={options}
                      snapshot={snap}
                      size={isCompact ? 168 : 220}
                    />
                  )}

                  {customerPhotoImageUrl && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        border: "1px solid #dcfce7",
                        background: "#f0fdf4",
                        borderRadius: 10,
                        padding: 10,
                      }}
                    >
                      <img
                        src={customerPhotoImageUrl}
                        alt="Custom print"
                        style={{
                          width: 52,
                          height: 52,
                          borderRadius: 8,
                          objectFit: "cover",
                          border: "1px solid #bbf7d0",
                        }}
                      />
                      <div style={{ fontSize: 12, color: "#166534" }}>
                        Customer uploaded custom photo
                      </div>
                    </div>
                  )}

                  {(cat === "licenseplate" || cat === "license_plate") &&
                    (licenseCustomization?.image?.imageUrl ||
                      legacyLicenseCustomization?.imageUrl ||
                      licenseCustomization?.text?.value) && (
                      <div
                        style={{
                          border: "1px solid #d1d5db",
                          background: "#fff",
                          borderRadius: 10,
                          padding: 10,
                          width: "100%",
                          boxSizing: "border-box",
                          minWidth: 0,
                        }}
                      >
                        <div style={{ fontSize: 12, color: "#374151", marginBottom: 6 }}>
                          License Preview
                        </div>
                        <div
                          style={{
                            width: "100%",
                            maxWidth: isCompact ? 210 : 240,
                            height: isCompact ? 105 : 120,
                          }}
                        >
                          <LicensePlatePreview
                            value={{
                              imageUrl:
                                licenseCustomization?.image?.imageUrl ||
                                legacyLicenseCustomization?.imageUrl ||
                                "",
                              scale: Number(
                                licenseCustomization?.image?.scale ??
                                  legacyLicenseCustomization?.scale ??
                                  1
                              ),
                              rotation: Number(
                                licenseCustomization?.image?.rotation ??
                                  legacyLicenseCustomization?.rotation ??
                                  0
                              ),
                              offsetXPct: Number(
                                licenseCustomization?.image?.offsetXPct ??
                                  legacyLicenseCustomization?.offsetXPct ??
                                  0
                              ),
                              offsetYPct: Number(
                                licenseCustomization?.image?.offsetYPct ??
                                  legacyLicenseCustomization?.offsetYPct ??
                                  0
                              ),
                              text: String(
                                licenseCustomization?.text?.value ??
                                  legacyLicenseCustomization?.text ??
                                  ""
                              ),
                              textFont: String(
                                licenseCustomization?.text?.font ??
                                  legacyLicenseCustomization?.textFont ??
                                  "Impact"
                              ),
                              textSizePx: Number(
                                licenseCustomization?.text?.sizePx ??
                                  legacyLicenseCustomization?.textSizePx ??
                                  78
                              ),
                              textOffsetXPct: Number(
                                licenseCustomization?.text?.xPct ??
                                  legacyLicenseCustomization?.textOffsetXPct ??
                                  0
                              ),
                              textOffsetYPct: Number(
                                licenseCustomization?.text?.yPct ??
                                  legacyLicenseCustomization?.textOffsetYPct ??
                                  22
                              ),
                              textColor: String(
                                licenseCustomization?.text?.color ??
                                  legacyLicenseCustomization?.textColor ??
                                  "#111827"
                              ),
                              textWeight: Number(
                                licenseCustomization?.text?.weight ??
                                  legacyLicenseCustomization?.textWeight ??
                                  900
                              ),
                              textItalic: Boolean(
                                licenseCustomization?.text?.italic ??
                                  legacyLicenseCustomization?.textItalic ??
                                  false
                              ),
                              textStroke: Boolean(
                                licenseCustomization?.text?.stroke ??
                                  legacyLicenseCustomization?.textStroke ??
                                  false
                              ),
                              textUppercase: Boolean(
                                licenseCustomization?.text?.uppercase ??
                                  legacyLicenseCustomization?.textUppercase ??
                                  true
                              ),
                              textLetterSpacingPx: Number(
                                licenseCustomization?.text?.letterSpacingPx ??
                                  legacyLicenseCustomization?.textLetterSpacingPx ??
                                  1
                              ),
                            }}
                          />
                        </div>
                      </div>
                    )}

                  {(cat === "phonecover" || cat === "phone_cover") &&
                    (phoneCaseCustomization?.imageUrl ||
                      phoneCoverCustomization?.image?.imageUrl ||
                      phoneCaseCustomization?.text ||
                      phoneCoverCustomization?.text?.value) && (
                    <div
                      style={{
                        border: "1px solid #d1d5db",
                        background: "#fff",
                        borderRadius: 10,
                        padding: 10,
                        width: "100%",
                        boxSizing: "border-box",
                        minWidth: 0,
                      }}
                    >
                      <div style={{ fontSize: 12, color: "#374151", marginBottom: 6 }}>
                        Phone Case Preview
                      </div>
                      <div
                        style={{
                          position: "relative",
                          width: "100%",
                          maxWidth: isCompact ? 150 : 210,
                          aspectRatio: "0.55 / 1",
                          border: "1px solid #9ca3af",
                          borderRadius: 22,
                          background: "#fff",
                          overflow: "hidden",
                        }}
                      >
                        {(phoneCaseCustomization?.imageUrl ||
                          phoneCoverCustomization?.image?.imageUrl) && (
                          <img
                            src={assetUrl(
                              phoneCaseCustomization?.imageUrl ||
                                phoneCoverCustomization?.image?.imageUrl ||
                                ""
                            )}
                            alt="Case art"
                            style={{
                              position: "absolute",
                              left: "50%",
                              top: "50%",
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              transform: `translate(calc(-50% + ${
                                Number(
                                  phoneCaseCustomization?.offsetXPct ??
                                    phoneCoverCustomization?.image?.offsetXPct ??
                                    0
                                ) * 0.9
                              }%), calc(-50% + ${
                                Number(
                                  phoneCaseCustomization?.offsetYPct ??
                                    phoneCoverCustomization?.image?.offsetYPct ??
                                    0
                                ) * 0.9
                              }%)) rotate(${
                                Number(
                                  phoneCaseCustomization?.rotation ??
                                    phoneCoverCustomization?.image?.rotation ??
                                    0
                                ) || 0
                              }deg) scale(${
                                Number(
                                  phoneCaseCustomization?.scale ??
                                    phoneCoverCustomization?.image?.scale ??
                                    1
                                ) || 1
                              })`,
                            }}
                          />
                        )}

                        {!!(
                          phoneCaseCustomization?.text ||
                          phoneCoverCustomization?.text?.value
                        ) && (
                          <div
                            style={{
                              position: "absolute",
                              left: "50%",
                              top: "50%",
                              color:
                                phoneCaseCustomization?.textColor ||
                                phoneCoverCustomization?.text?.color ||
                                phoneCaseTextStyle?.color ||
                                "#111827",
                              fontFamily:
                                phoneCaseCustomization?.textFont ||
                                phoneCoverCustomization?.text?.font ||
                                phoneCaseTextStyle?.font ||
                                "sans-serif",
                              fontWeight:
                                phoneCaseCustomization?.textWeight ||
                                phoneCoverCustomization?.text?.weight ||
                                phoneCaseTextStyle?.weight ||
                                700,
                              fontStyle:
                                phoneCaseCustomization?.textItalic ||
                                phoneCoverCustomization?.text?.italic ||
                                phoneCaseTextStyle?.italic
                                  ? "italic"
                                  : "normal",
                              fontSize: Math.max(
                                10,
                                Number(
                                  phoneCaseCustomization?.textSizePx ||
                                    phoneCoverCustomization?.text?.sizePx ||
                                    phoneCaseTextStyle?.sizePx ||
                                    42
                                ) * 0.24
                              ),
                              whiteSpace: "nowrap",
                              textTransform:
                                phoneCaseCustomization?.textUppercase ||
                                phoneCoverCustomization?.text?.uppercase ||
                                phoneCaseTextStyle?.uppercase
                                  ? "uppercase"
                                  : "none",
                              letterSpacing:
                                Number(
                                  phoneCaseCustomization?.textLetterSpacingPx ||
                                    phoneCoverCustomization?.text?.letterSpacingPx ||
                                    phoneCaseTextStyle?.letterSpacingPx ||
                                    0
                                ) * 0.2,
                              transform: `translate(calc(-50% + ${
                                Number(
                                  phoneCaseCustomization?.textOffsetXPct ??
                                    phoneCoverCustomization?.text?.xPct ??
                                    phoneCaseTextStyle?.xPct ??
                                    0
                                ) * 0.9
                              }%), calc(-50% + ${
                                Number(
                                  phoneCaseCustomization?.textOffsetYPct ??
                                    phoneCoverCustomization?.text?.yPct ??
                                    phoneCaseTextStyle?.yPct ??
                                    30
                                ) * 0.9
                              }%))`,
                            }}
                          >
                            {String(
                              phoneCaseCustomization?.text ||
                                phoneCoverCustomization?.text?.value ||
                                ""
                            )}
                          </div>
                        )}

                        <div
                          style={{
                            position: "absolute",
                            left: `${
                              Number(
                                phoneCoverCustomization?.cameraCutout?.xPct ??
                                  snap?.cameraCutout?.xPct ??
                                  7.2
                              ) + Number(
                                phoneCoverCustomization?.cameraCutout?.widthPct ??
                                  snap?.cameraCutout?.widthPct ??
                                  26
                              ) / 2
                            }%`,
                            top: `${
                              Number(
                                phoneCoverCustomization?.cameraCutout?.yPct ??
                                  snap?.cameraCutout?.yPct ??
                                  5.2
                              ) + Number(
                                phoneCoverCustomization?.cameraCutout?.heightPct ??
                                  snap?.cameraCutout?.heightPct ??
                                  18
                              ) / 2
                            }%`,
                            width: `${
                              Number(
                                phoneCoverCustomization?.cameraCutout?.widthPct ??
                                  snap?.cameraCutout?.widthPct ??
                                  26
                              )
                            }%`,
                            height: `${
                              Number(
                                phoneCoverCustomization?.cameraCutout?.heightPct ??
                                  snap?.cameraCutout?.heightPct ??
                                  18
                              )
                            }%`,
                            transform: "translate(-50%, -50%)",
                            border: "1px solid #9ca3af",
                            borderRadius: `${
                              Number(
                                phoneCoverCustomization?.cameraCutout
                                  ?.cornerRadiusPct ??
                                  snap?.cameraCutout?.cornerRadiusPct ??
                                  16
                              ) * 0.35
                            }%`,
                            background: "#fff",
                          }}
                        />
                      </div>
                    </div>
                    )}

                  {cat === "tshirt" &&
                    (tshirtCustomization?.front ||
                      tshirtCustomization?.back ||
                      tshirtCustomization?.color) && (
                      <TshirtCustomizationPreview
                        tshirtCustomization={tshirtCustomization}
                        fallbackColor={String(
                          tshirtCustomization?.color ||
                            (options as any).color ||
                            "#ffffff"
                        )}
                      />
                    )}

                  {cat === "cup" &&
                    (cupCustomization?.image?.imageUrl ||
                      photoCustomization?.imageUrl ||
                      cupCustomization?.image?.text) && (
                      <div
                        style={{
                          border: "1px solid #d1d5db",
                          background: "#fff",
                          borderRadius: 10,
                          padding: 10,
                          width: "100%",
                          boxSizing: "border-box",
                          minWidth: 0,
                        }}
                      >
                        <div style={{ fontSize: 12, color: "#374151", marginBottom: 6 }}>
                          Cup Preview (drag to spin)
                        </div>
                        <div
                          style={{
                            width: "100%",
                            maxWidth: isCompact ? 220 : 260,
                            height: isCompact ? 160 : 190,
                          }}
                        >
                          <Cup3DPreview
                            value={cupCustomization?.image || photoCustomization || null}
                            viewMode="default"
                          />
                        </div>
                      </div>
                    )}

                  {cat === "engraving" && engravingCustomization?.front ? (
                    <div
                      style={{
                        border: "1px solid #d1d5db",
                        background: "#fff",
                        borderRadius: 10,
                        padding: 10,
                        width: "100%",
                        boxSizing: "border-box",
                        minWidth: 0,
                      }}
                    >
                      <div style={{ fontSize: 12, color: "#374151", marginBottom: 6 }}>
                        Engraving Preview
                      </div>
                      <EngravingCustomizationPreview
                        selection={engravingCustomization}
                        mode={
                          engravingCustomization?.back?.enabled &&
                          String(
                            engravingCustomization?.back?.sanitizedText ||
                              engravingCustomization?.back?.text ||
                              ""
                          ).trim()
                            ? "split"
                            : "single"
                        }
                        activeSide={engravingCustomization?.activeSide}
                        showSideLabels={
                          Boolean(engravingCustomization?.back?.enabled) &&
                          !!String(
                            engravingCustomization?.back?.sanitizedText ||
                              engravingCustomization?.back?.text ||
                              ""
                          ).trim()
                        }
                      />
                    </div>
                  ) : null}

                  {sourceAssets.length > 0 && (
                    <div
                      style={{
                        border: "1px solid #e2e8f0",
                        background: "#f8fafc",
                        borderRadius: 10,
                        padding: 10,
                        minWidth: 0,
                        boxSizing: "border-box",
                      }}
                    >
                      <div
                        style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}
                      >
                        Source Uploads (Printable Originals)
                      </div>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fit,minmax(140px,1fr))",
                          gap: 6,
                        }}
                      >
                        {sourceAssets.map((asset) => (
                          <div
                            key={asset.key}
                            style={{
                              border: "1px solid #cbd5e1",
                              borderRadius: 8,
                              padding: "6px 8px",
                              background: "#fff",
                              minWidth: 0,
                            }}
                          >
                            <div style={{ fontSize: 12, color: "#475569" }}>
                              {asset.label}
                            </div>
                            <a
                              href={asset.url}
                              target="_blank"
                              rel="noreferrer"
                              download
                              style={{
                                display: "inline-block",
                                marginTop: 4,
                                fontSize: 12,
                                color: "#2563eb",
                                textDecoration: "underline",
                                wordBreak: "break-all",
                              }}
                            >
                              {asset.filename || "Open / Download"}
                            </a>
                            {(asset.mime || asset.size) && (
                              <div style={{ marginTop: 2, fontSize: 11, color: "#64748b" }}>
                                {asset.mime || ""}{" "}
                                {asset.size ? `• ${Math.round(asset.size / 1024)} KB` : ""}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Selected Options from client */}
                  {readableOptions.length > 0 && (
                    <div
                      style={{
                        border: "1px solid #e5e7eb",
                        borderRadius: 10,
                        padding: 10,
                        background: "#fff",
                        minWidth: 0,
                        boxSizing: "border-box",
                      }}
                    >
                      <div
                        style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}
                      >
                        Selected Options
                      </div>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fit,minmax(160px,1fr))",
                          gap: 8,
                        }}
                      >
                        {readableOptions.map((option) => (
                          <div
                            key={option.label}
                            style={{
                              fontSize: 12,
                              background: "#f9fafb",
                              border: "1px solid #eef2f7",
                              borderRadius: 8,
                              padding: "8px 10px",
                              minWidth: 0,
                              overflowWrap: "anywhere",
                            }}
                          >
                            <span style={{ color: "#6b7280" }}>
                              {option.label}:
                            </span>{" "}
                            <span style={{ fontWeight: 600 }}>
                              {option.value}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

            </div>
          );
        })}
      </div>
      <div style={{ height: 16 }} />
      {/* --- NOTES & TAGS + CUSTOM EMAIL --- */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isCompact ? "minmax(0, 1fr)" : "1fr 1fr",
          gap: 16,
        }}
      >
        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            padding: 16,
            minWidth: 0,
          }}
        >
          <h3 style={{ marginTop: 0 }}>Notes & Tags</h3>
          <textarea
            placeholder="Internal notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            style={{
              padding: 8,
              border: "1px solid #e5e7eb",
              borderRadius: 8,
              width: "100%",
              boxSizing: "border-box",
            }}
          />
          <div style={{ height: 8 }} />
          <input
            placeholder="Tags (comma-separated)"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            style={{
              padding: 8,
              border: "1px solid #e5e7eb",
              borderRadius: 8,
              width: "100%",
            }}
          />
          <div style={{ height: 8 }} />
          <button
            onClick={saveMeta}
            style={{
              padding: "8px 12px",
              borderRadius: 8,
              border: "1px solid #e5e7eb",
              background: "#111827",
              color: "white",
              fontWeight: 600,
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              cursor: "pointer",
            }}
          >
            Save Meta
          </button>
        </div>

        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            padding: 16,
            minWidth: 0,
          }}
        >
          <h3 style={{ marginTop: 0 }}>Email Customer (Manual)</h3>

          <div style={{ display: "grid", gap: 8 }}>
            <input
              placeholder="Subject"
              value={customSubject}
              onChange={(e) => setCustomSubject(e.target.value)}
              style={{
                padding: 8,
                border: "1px solid #e5e7eb",
                borderRadius: 8,
              }}
            />
            <textarea
              placeholder="Message (HTML supported)"
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              rows={6}
              style={{
                padding: 8,
                border: "1px solid #e5e7eb",
                borderRadius: 8,
              }}
            />
            <label
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                fontSize: 14,
              }}
            >
              <input
                type="checkbox"
                checked={includeSummary}
                onChange={(e) => setIncludeSummary(e.target.checked)}
              />
              Include order summary + branding
            </label>
            <button
              onClick={sendCustom}
              disabled={sendingCustom || !customerEmail}
              title={customerEmail ? "Send manual email" : "No email on file"}
              style={{
                padding: "8px 12px",
                borderRadius: 8,
                border: "1px solid #e5e7eb",
                background: sendingCustom ? "#6b7280" : "#111827",
                color: "white",
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                cursor: sendingCustom ? "not-allowed" : "pointer",
                width: "fit-content",
              }}
            >
              {sendingCustom && <Spinner />} Send Manual Email
            </button>
          </div>
        </div>
      </div>

      <div style={{ height: 16 }} />

      <div
        style={{
          border: "1px solid #d2d2d7",
          borderRadius: 12,
          padding: isCompact ? 14 : 16,
          background: "#fff",
          display: "flex",
          gap: 10,
          alignItems: isCompact ? "stretch" : "center",
          justifyContent: "space-between",
          flexDirection: isCompact ? "column" : "row",
          minWidth: 0,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, color: "#1d1d1f" }}>
            Share Order Details
          </div>
          {orderShareStatus ? (
            <div style={{ color: "#047857", fontSize: 12, fontWeight: 700, marginTop: 3 }}>
              {orderShareStatus}
            </div>
          ) : null}
        </div>
        <button
          type="button"
          onClick={shareOrderDetails}
          style={{
            padding: "10px 14px",
            borderRadius: 999,
            border: "1px solid #111827",
            background: "#111827",
            color: "white",
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer",
            width: isCompact ? "100%" : "auto",
          }}
        >
          Share Order Details
        </button>
      </div>

      {/* spinner keyframes */}
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
